import * as PIXI from "pixi.js";
import { publishGameViewArtWidth } from "../../../utils/gameViewArtWidth";
import { soundManager } from "../../../SoundManager";
// import { SOUND_NAME } from "../../../sounds";
import { SOUND_NAME, SOUND_VOLUMES } from "../../../sounds";
import { HowlCache } from "../../../State/Slices/soundsSlice";
import i18next from "i18next";
import {
  logoPotStage,
  onLogoPotChange,
  LOGO_POT_STAGES,
  LOGO_POT_WIN,
} from "../../../Games/Shared/logoPot";
// SkeletonJson / AtlasAttachmentLoader / TextureAtlas: the textureless coin
// counter spine (_coinCounterSpine).
import {
  Spine,
  SkeletonJson,
  AtlasAttachmentLoader,
  TextureAtlas,
} from "@esotericsoftware/spine-pixi-v7";
// import { Skin, Animation, SequenceTimeline } from "@esotericsoftware/spine-core";
import { Skin, Animation, SequenceTimeline, Physics, TranslateTimeline } from "@esotericsoftware/spine-core";
import { FEATURES_CONFIG } from "../../../featuresConfig";
import { PixiSettings } from "../../../pixiSettings";
import { Settings } from "../../../settings";
import SpineAtlasUploader from "../../../utils/SpineAtlasUploader";
import { queueMeshWarm } from "../../../Games/Shared/spineMeshWarm";
import { FONT_STYLES, scaleStyle } from "../../../fontStyles";
import { setSpinesDormant } from "../../../Games/Shared/spineDormancy";
import { formatToCustomLocale } from "../../../utilities";
import { createWinNumberText } from "../WinText/WinTextController";
import { PROVIDER_CONFIG } from "../../../RGS/Providers/PopiPlay/providerConfig";
import {
  playSceneAccent,
  playSceneAccentForWindow,
  stopSceneAccents,
} from "../../../Games/Shared/sceneAccents";

// ============================================================================
// SPINE CONTRACT
//
// Names this controller looks up in the skeletons. A re-export with new names
// only changes the tables below. Most entries are CANDIDATE LISTS, tried in
// order, first hit wins, so one renamed item does not silently render nothing.
// ============================================================================
// The coin anticipation counter ("4 / 6 Coins" — "Fireballs" in the GDD, renamed 2026-10-13; "Anticipation Hold &
// Win"): the win text spine injected into the hand-added
// `txt_slot_coin_counter` (game_view + free_spins_game_view, on the reels'
// bottom border), with the text in that spine's own text slot. Spine units.
const COIN_ANTICIPATION_COUNTER = {
  SLOT: "txt_slot_coin_counter",
  // The injected win text spine's own text slot.
  TEXT_SLOT: "txt_slot_value",
  FONT_SIZE: 48,
  MAX_WIDTH: 900,
  // The spine (the fire behind the text) is scaled by this and the text by
  // 1 / it, so only the fire grows.
  SPINE_SCALE: 1.3,
  KEY: "coin_anticipation_counter",
  // Caught in its `out` by a new count: back to the hold clip over this (s) —
  // the out's fade and shrink blend away, no `in` (showCoinCounter).
  RESUME_MIX: 0.25,
  // Hidden = this alpha, never `visible = false` (RULE #1: mounted and warm).
  HIDDEN_ALPHA: 0.001,
};

const SPINE_ASSETS = {
  SKELETON: "game_view-skeleton",
  ATLAS: "game_view-atlas",
};

// The logo spine (the Pot, GDD §5), injected into the logo slot. Default skin
// only; the scene's slot places and scales it.
const LOGO_ASSETS = {
  SKELETON: "logo-skeleton",
  ATLAS: "logo-atlas",
  // Each level clip contains the idle too: ONE level clip plays, looped. The
  // stage comes from setLogoStage (Games/Shared/logoPot); this list is only
  // the fallback when no stage clip is found.
  LEVEL_ANIMATIONS: ["logo_lvl1"],
  LEVEL_TRACK: 0,
  // Default blend (s) between the logo's clips. Level clips cut between each
  // other (mix 0, see attachLogo); a change that is not a rise blends with
  // this on its own entry (_playLogoRise).
  LEVEL_MIX: 0.4,
  // A coin's trail landing in the cup (GDD §5.1), over the level clip.
  HIT_ANIMATIONS: ["logo_hit"],
  HIT_TRACK: 2,
  // The WIN blast (GDD: "small blast animation around the fire and logo"),
  // on its own slots and track, over the level clip.
  BLAST_ANIMATIONS: ["logo_blast"],
  BLAST_TRACK: 1,
  // The fire bowl on top of the logo, where the coins' trails land.
  FIRE_BONES: ["logo_fire_00000", "logo_fire_00001"],
  // The fire flipbook on its own track (see prepareLogoFireLoop): its slots,
  // the clip built from them and its track.
  FIRE_SEQUENCE_SLOTS: ["logo_fire_00000", "logo_fire_00001"],
  FIRE_LOOP: "logo_fire_loop",
  FIRE_LOOP_TRACK: 3,
};

/**
 * Moves the fire flipbook out of every logo clip into one endless loop clip.
 *
 * Every level / _in / _reel clip carries the same two 30-frame sequences
 * (1 s), so a clip starting mid-loop would restart the flames at frame 0, and
 * spine does not blend flipbook frames. The sequence timelines are removed
 * from all clips and played once, looped, on FIRE_LOOP_TRACK, so the flames
 * never restart. Runs once per skeleton data (both rooms' logos share it), at
 * runtime so a re-export cannot undo it.
 *
 * @returns {boolean} the loop exists
 */
const prepareLogoFireLoop = (skeletonData) => {
  if (!skeletonData) return false;
  if (skeletonData.__logoFireLoopReady) {
    return !!skeletonData.findAnimation(LOGO_ASSETS.FIRE_LOOP);
  }
  skeletonData.__logoFireLoopReady = true;
  const slotIndices = new Set(
    LOGO_ASSETS.FIRE_SEQUENCE_SLOTS.map((name) => skeletonData.findSlot(name)?.index).filter(
      (index) => index !== undefined,
    ),
  );
  if (!slotIndices.size) return false;
  const isFireSequence = (timeline) =>
    timeline instanceof SequenceTimeline && slotIndices.has(timeline.slotIndex);
  // The loop takes its timelines from the first clip that has them all.
  const source = skeletonData.animations.find(
    (animation) => animation.timelines.filter(isFireSequence).length === slotIndices.size,
  );
  if (!source) return false;
  const loop = new Animation(
    LOGO_ASSETS.FIRE_LOOP,
    source.timelines.filter(isFireSequence),
    // 30 frames x 1/30 s — the clips' own length, one turn of the flipbook.
    1,
  );
  skeletonData.animations.forEach((animation) => {
    if (animation.timelines.some(isFireSequence)) {
      animation.setTimelines(animation.timelines.filter((timeline) => !isFireSequence(timeline)));
    }
  });
  skeletonData.animations.push(loop);
  return true;
};

// The jackpot plates' hit clips (jackpot_<tier>_hit): one track per tier,
// above idle (0), accents (1, 2) and the anticipation (3).
const JACKPOT_HIT_TIERS = ["mini", "minor", "midi", "major", "grand", "royal"];
const JACKPOT_HIT_TRACK_BASE = 4;
// A lit plate's fade-out at the next spin, s.
const JACKPOT_HIT_RELEASE_S = 0.25;
// The Random Pot's logo flight over the reels (logo_reel_*), above the
// jackpot hit tracks (4–9).
const LOGO_REEL_TRACK = 10;

// The room's background darkened so the reels, jackpot plates and logo stand
// out (Vladimir, 2026-10-12 — the Random Pot's presentation spin). The
// hand-added `bg_darken` slot in game_view.json: fill_main's art tinted
// black, drawn after the background and before the reels, faded to the coins'
// darken depth (828282 -> a 7d veil) over the coins' 1 s by its clips.
const BG_DARKEN = {
  TRACK: 11,
  IN: "bg_darken_in",
  OUT: "bg_darken_out",
  // A darken cut short by the lighten blends into it, no jump.
  MIX: 0.2,
};

// ── The drums take over (Vladimir, 2026-10-22) ── while the room is dark the
// room's music steps DOWN and the drums carry it; at the end the drums are not
// cut or faded — they finish the bar they are on — and the music comes back
// up after them.
const ANTICIPATION_DRUMS = {
  // DUCK: 0.5, // the room track's own volume x this under the drums (-6 dB)
  // DUCK_MS: 300,
  // RESTORE_MS: 600,
  // "still barely hear drums … music must not stop instantly" (2026-10-22):
  // further down, and gliding — the duck over the darken's own 1 s.
  DUCK: 0.25, // -12 dB
  DUCK_MS: 1000,
  RESTORE_MS: 1500,
  // Hold & Win's fire loop fades over this at the end (the base game's fades
  // over its board fire's own out, 1 s).
  FIRE_FADE_MS: 1000,
  // The drums' fade at the end — the music comes back over the same time.
  DRUMS_FADE_MS: 1500,
  // ── Intro once, then the cycle on loop (2026-10-22) ── anticipation_drums_in
  // is the designer's intro (0.984 s), anticipation_drums ONE drum cycle
  // (1.188 s) that loops on itself; the loop starts as the intro ends.
  IN_MS: 984,
  // The end hit (anticipation_end, "du-du DUM") lands and the drums go out
  // under it this fast.
  UNDER_HIT_MS: 150,
  // Whichever room track is up (only one ever is).
  ROOM_TRACKS: [
    SOUND_NAME.hold_and_win_music,
    SOUND_NAME.feature_background_music,
    SOUND_NAME.background_music,
    SOUND_NAME.ambient_music,
  ],
};

// The accent tables and the three functions behind playAccent /
// playAccentForWindow / stopAccent. Re-exported so a caller that already names
// this scene keeps one import.
export { SCENE_ACCENTS, SCENE_ACCENT_TRACKS } from "../../../Games/Shared/sceneAccents";

// The jackpot plates' value text (setJackpotValues), in spine units; the
// plate slot scales it. The default for a tier not listed below.
const JACKPOT_PLATE_TEXT = { FONT_SIZE: 52, MAX_WIDTH: 200 };
// Per tier: the plates differ in size (art widths: royal 300, grand 287,
// major / midi 269, minor / mini 231). Font by rank, width ~80% of the art.
const JACKPOT_PLATE_TEXT_BY_TIER = {
  royal: { FONT_SIZE: 52, MAX_WIDTH: 240 },
  grand: { FONT_SIZE: 52, MAX_WIDTH: 230 },
  major: { FONT_SIZE: 46, MAX_WIDTH: 215 },
  midi: { FONT_SIZE: 46, MAX_WIDTH: 215 },
  minor: { FONT_SIZE: 40, MAX_WIDTH: 185 },
  mini: { FONT_SIZE: 40, MAX_WIDTH: 185 },
};
// Multiplier on the `jackpots` style's outline (scaled to the plate font). At
// plate sizes a thinner outline is ~1 screen pixel or less on small screens
// (PIXI draws half of it under the fill) and breaks up. Plates only.
const JACKPOT_PLATE_STROKE_BOOST = 1.5;
// The plates that show their MULTIPLIER of the bet ("5000x") instead of the
// money (PO, 2026-10-12: the Royal). Every room's plates (setJackpotValues).
// const JACKPOT_PLATE_AS_MULTIPLIER = ["royal"];
// None now (2026-10-30): the Royal shows its money too — bet x 5000, the same
// format and sizing as every other plate.
const JACKPOT_PLATE_AS_MULTIPLIER = [];

/** Prefix for this scene's warnings. */
const TAG = "GameViewSimple";

/**
 * The base reel set in the game view spine's own (native) units. The reel
 * container inherits the spine's scale, so these never change on resize. One
 * source for the reels, their dim and anything placed per reel in spine space
 * (the anticipation pitch).
 */
const NATIVE_DIVIDERS = FEATURES_CONFIG.DIVIDERS?.ENABLED
  ? { width: 4, height: Settings.SYMBOL_HEIGHT * Settings.SYMBOLS_VISIBLE }
  : { width: 0, height: 0 };
export const NATIVE_REELSET_SIZE = Object.freeze({
  symbolWidth: Settings.SYMBOL_WIDTH,
  symbolHeight: Settings.SYMBOL_HEIGHT,
  dividerDimensions: NATIVE_DIVIDERS,
  reelSpacing: Settings.SYMBOL_WIDTH + NATIVE_DIVIDERS.width,
});

const SPINE_CONTRACT = {
  // The scene's looping idle.
  IDLE_ANIMATIONS: ["idle", "base_idle", "main_idle", "Main_bg_idle"],

  // Layout skins, one per orientation. Combined with "default" — see applySkin.
  LANDSCAPE_SKINS: ["resize/landscape", "landscape"],
  PORTRAIT_SKINS: ["resize/portrait", "portrait"],

  // Where the spinning reels go, and where the symbol overlay above them goes.
  // Both are SLOTS: the animator places them in the draw order and we inject
  // at that point, so the layering needs no tuning here. A slot may carry its
  // own art (reel background, frame); spine-pixi draws the slot object on top
  // of it. Base game: *_maingame, free spins: *_freegame, Hold & Win: *_hnw.
  // The *_placeholder names stay for the Hold & Win spine.
  REEL_SLOTS: [
    "attachment_reelbg_maingame",
    "attachment_reelbg_freegame",
    "attachment_reelbg_hnw",
    "attachment_slot_placeholder",
  ],
  OVERLAY_SLOTS: [
    // Not attachment_overlay_hnw (in the H&W JSON, unused): above the lock
    // plates it would lift the locked rows' coins over them.
    "attachment_reelframe_maingame",
    "attachment_reelframe_freegame",
    "attachment_reelframe_hnw",
    "attachment_slot_placeholder_front",
  ],

  // Per-reel scatter anticipation (GDD §6.1), older exports: the clips live in
  // this skeleton for ONE column, on the `anticipation` bone (a child of
  // `reels`). No clip keys that bone, so it is moved to the anticipating reel
  // before a clip plays (one reel at a time). Its own track, clear of the
  // idle (0) and the accents (1, 2). ANTICIPATION_SLOT_PREFIX names every slot
  // the clips draw, so they can be reset (hidden) when the chain ends.
  ANTICIPATION_BONES: ["anticipation"],
  ANTICIPATION_IN: ["anticipation_in"],
  ANTICIPATION_LOOP: ["anticipation_loop"],
  ANTICIPATION_OUT: ["anticipation_out"],
  ANTICIPATION_SLOT_PREFIX: "ant_",
  ANTICIPATION_TRACK: 3,
  // Current exports: the anticipation is its own spine, played in this slot.
  // Its column is centred on its origin; the slot's bone is moved over the
  // reel window (the reel slot's bone) and stepped per reel.
  ANTICIPATION_SLOTS: ["attachment_anticipation"],
  ANTICIPATION_SPINE: { SKELETON: "anticipation-skeleton", ATLAS: "anticipation-atlas" },
  // The reel anticipation: a frame over the whole board, its own spine, in
  // this slot (drawn above the reel frame). Up for the whole chain: in → loop
  // at its start, out at its end.
  REEL_ANTICIPATION_SLOTS: ["attachment_reel_anticipation"],
  REEL_ANTICIPATION_SPINE: {
    SKELETON: "reel_anticipation-skeleton",
    ATLAS: "reel_anticipation-atlas",
  },
  // Parts of the reel anticipation drawn with no art (Vladimir, 2026-10-12 —
  // first a trial, kept: "looks great"): the four gold corner glows and the
  // gold line along the top. [] = everything shows. The animator should drop
  // them from the export (animator list).
  // REEL_ANTICIPATION_HIDDEN_SLOTS: ["reels_ant", "reels_ant2", "reels_ant3", "reels_ant4", "ant_line"],
  // + the two torch flames lying along the top bar, one from each corner
  // (torch_00009 right, torch_000019 left — Vladimir, 2026-10-12).
  REEL_ANTICIPATION_HIDDEN_SLOTS: [
    "reels_ant",
    "reels_ant2",
    "reels_ant3",
    "reels_ant4",
    "ant_line",
    "torch_00009",
    "torch_000019",
    // ...and the two small fireballs at the top corners (right, left).
    "fireball_up_00001",
    "fireball_up_00004",
  ],
  REEL_ANTICIPATION_IN: ["reel_anticipation_in"],
  REEL_ANTICIPATION_LOOP: ["reel_anticipation_loop"],
  REEL_ANTICIPATION_OUT: ["reel_anticipation_out"],

  // Where the game logo goes. The slot's bone carries position and scale per
  // orientation (the layout skins switch it) and the logo is centred on its
  // own origin, so nothing is tuned in code. Current exports have one slot
  // per layout; LOGO_SLOTS is the shared slot of older ones.
  LOGO_SLOTS: ["logo_placeholder"],
  LOGO_SLOTS_LANDSCAPE: ["attachment_logo_landscape"],
  LOGO_SLOTS_PORTRAIT: ["attachment_logo_portrait"],

  // Fallback only, for a skeleton that marks the reel position with a bare bone
  // instead of a slot. Then the container has to follow the bone by hand and
  // carry a forced zIndex — see _attachToBone.
  REEL_BONES: ["attachment_slot_placeholder"],
  REEL_BEHIND_SLOTS: ["reel_bg_50%2", "main_reel_bg"],

  // The bone that parents the whole reel block — frame, keyboard, screen mesh,
  // background plate AND the injection point. Scaling it resizes the board
  // relative to the room around it. See GAME_VIEW_REEL_SCALE_CONFIG.
  REEL_BLOCK_BONES: ["reels"],
};

// ============================================================================
// SCENE OFFSET — per layout
//
// Moves the WHOLE scene: background art, reel frame and the injected reels all
// together, because they are one spine.
//
// Keyed by LAYOUT, not by device. Same ten keys as
// PixiSettings.REELSET_LAYOUT_CONFIGS, so the padding table and this table line
// up one-to-one. Device alone could not express this — it cannot tell a small
// desktop from an ultra-wide one, and those need different offsets.
//
// Units are multiples of symbolHeight, so an offset holds its proportion when
// the board resizes.
//
//   y: POSITIVE pushes the scene UP.     ← same sign as GAME_CONTAINER_OFFSET_CONFIG
//   x: POSITIVE pushes the scene RIGHT.
//
// A plain number is shorthand for y-only, which is the common case.
// ============================================================================
// CENTER_FIX_X shifts every layout sideways, given in spine units and
// converted to cells so it scales with the scene. 0 = no shift. Both rooms
// read this table (FreeSpinsGameViewController extends this class).
const CENTER_FIX_X = 0 / Settings.SYMBOL_HEIGHT;

export const GAME_VIEW_OFFSET_CONFIG = {
  MOBILE_SMALL_PORTRAIT: { x: CENTER_FIX_X, y: -0.3 }, // down 0.3 symbol: clears the top bar and menu / sound buttons
  // MOBILE_SMALL_LANDSCAPE: { x: CENTER_FIX_X, y: 0 },
  // MOBILE_SMALL_LANDSCAPE: { x: CENTER_FIX_X, y: -0.05 }, // as MOBILE_LANDSCAPE (2026-10-16)
  // MOBILE_SMALL_LANDSCAPE: { x: CENTER_FIX_X, y: -0.1 }, // the bigger reel: same gap above and below
  MOBILE_SMALL_LANDSCAPE: { x: CENTER_FIX_X, y: -0.08 }, // same gap above and below (2026-10-16)
  MOBILE_PORTRAIT: { x: CENTER_FIX_X, y: 0 },
  // MOBILE_LANDSCAPE: { x: CENTER_FIX_X, y: 0 },
  // MOBILE_LANDSCAPE: { x: CENTER_FIX_X, y: -0.05 }, // a hair down: same gap above and below (2026-10-16)
  // MOBILE_LANDSCAPE: { x: CENTER_FIX_X, y: -0.1 }, // as MOBILE_SMALL_LANDSCAPE (2026-10-16)
  MOBILE_LANDSCAPE: { x: CENTER_FIX_X, y: -0.08 }, // as MOBILE_SMALL_LANDSCAPE
  // TABLET_PORTRAIT: { x: CENTER_FIX_X, y: 0 },
  // TABLET_PORTRAIT: { x: CENTER_FIX_X, y: -0.1 }, // an iPad upright: a bit down (2026-10-29)
  // TABLET_PORTRAIT: { x: CENTER_FIX_X, y: -0.4 }, // the plates clear of the menu / sound row
  TABLET_PORTRAIT: { x: CENTER_FIX_X, y: -0.27 }, // in the middle between the top buttons and BONUS (2026-10-29)
  // TABLET_LANDSCAPE: { x: CENTER_FIX_X, y: 0 },
  // TABLET_LANDSCAPE: { x: CENTER_FIX_X, y: -0.13 }, // an iPad sideways (2026-10-29): MOBILE_LANDSCAPE's -0.08, a tiny bit down
  // TABLET_LANDSCAPE: { x: CENTER_FIX_X, y: -0.17 }, // a tiny bit further down, reel and jackpots
  TABLET_LANDSCAPE: { x: CENTER_FIX_X, y: -0.21 }, // a tiny bit more
  SMALL_DESKTOP: { x: CENTER_FIX_X, y: 0 },
  // DESKTOP: { x: CENTER_FIX_X, y: 0 },
  DESKTOP: { x: CENTER_FIX_X, y: -0.05 }, // a hair down: same gap above and below (2026-10-16)
  WIDE_DESKTOP: { x: CENTER_FIX_X, y: 0 },
  ULTRA_WIDE_DESKTOP: { x: CENTER_FIX_X, y: 0 },
};

// ============================================================================
// SCENE ZOOM — per layout
//
// Multiplier on top of the engine's own scale (the reel-area fit). 1.0 is
// untouched. The knob for "make the whole thing bigger": it zooms everything
// uniformly — background, frame and reels, which are children of the spine
// and inherit its scale.
//
// Same ten keys as GAME_VIEW_OFFSET_CONFIG. All 1.0 in this game.
// ============================================================================
export const GAME_VIEW_ZOOM_CONFIG = {
  MOBILE_SMALL_PORTRAIT: 1.0,
  MOBILE_SMALL_LANDSCAPE: 1.0,
  MOBILE_PORTRAIT: 1.0,
  MOBILE_LANDSCAPE: 1.0,
  TABLET_PORTRAIT: 1.0,
  TABLET_LANDSCAPE: 1.0,
  SMALL_DESKTOP: 1.0,
  DESKTOP: 1.0,
  WIDE_DESKTOP: 1.0,
  // ULTRA_WIDE_DESKTOP: 1.0,
  // ULTRA_WIDE_DESKTOP: 1.1, // closer: less air above the reels, wider art (2026-10-22)
  // ULTRA_WIDE_DESKTOP: 1.2, // further (2026-10-22)
  ULTRA_WIDE_DESKTOP: 1.25, // a nudge more (2026-10-22)
};

const resolveZoom = (layout) => GAME_VIEW_ZOOM_CONFIG[layout] ?? 1;

// coverBackground's limit: how far the background may grow past its art's own
// size to reach the window's edges. 1 = never past the art (2026-10-22).
const BG_COVER_MAX_GROW = 1;

// ============================================================================
// LANDSCAPE JACKPOT COLUMN — per layout (2026-10-16)
//
// Moves the landscape jackpot plates and the logo together, apart from the
// reels and the background. Multiples of SYMBOL_HEIGHT; y: POSITIVE = UP (the
// same sign as GAME_VIEW_OFFSET_CONFIG). Base and free-spins rooms only — the
// Hold & Win room has its own layout. Applied each frame on top of the
// animation (applyColumnOffset), so the spine's own positions stay as they are.
// ============================================================================
export const LANDSCAPE_COLUMN_OFFSET_CONFIG = {
  // Down: the logo's fire ran into the clock / game name at the top left,
  // and there was room above the toolbar.
  MOBILE_LANDSCAPE: { y: -0.23 },
  MOBILE_SMALL_LANDSCAPE: { y: -0.23 }, // as MOBILE_LANDSCAPE
  TABLET_LANDSCAPE: { y: -0.23 }, // as MOBILE_LANDSCAPE — an iPad sideways (2026-10-29)
};
// The BASE room's column, as its spine sets it up — published once it is
// built, so the Hold & Win room can draw its own column at the same place and
// size (HoldAndWinGameViewController.applyColumnMatch). name → {x, y, scaleX, scaleY}.
let baseColumnSetup = null;
export const getBaseColumnSetup = () => baseColumnSetup;

export const LANDSCAPE_COLUMN_BONES = [
  "land_jckp_royal",
  "land_jckp_grand",
  "land_jckp_major",
  "land_jckp_midi",
  "land_jckp_minor",
  "land_jckp_mini",
  "attachment_logo_landscape",
];
const LANDSCAPE_LOGO_BONE = "attachment_logo_landscape";
const LANDSCAPE_LOGO_FLIGHT = "logo_reel_landscape";

// ============================================================================
// REEL BLOCK SCALE — per layout
//
// Resizes the reel block INSIDE the scene: frame, keyboard, screen mesh,
// background plate and the reels. 1.0 is the skeleton as exported. The
// opposite of the zoom above:
//
//   GAME_VIEW_ZOOM_CONFIG        grows the whole scene, room included
//   GAME_VIEW_REEL_SCALE_CONFIG  shrinks the board WITHIN the room
//
// Applied to the `reels` bone at runtime, so a re-export cannot drop it.
// All 1.0 in this game: the board is never scaled apart from the background.
// ============================================================================
export const GAME_VIEW_REEL_SCALE_CONFIG = {
  MOBILE_SMALL_PORTRAIT: 1.0,
  MOBILE_SMALL_LANDSCAPE: 1.0,
  MOBILE_PORTRAIT: 1.0,
  MOBILE_LANDSCAPE: 1.0,
  TABLET_PORTRAIT: 1.0,
  TABLET_LANDSCAPE: 1.0,
  SMALL_DESKTOP: 1.0,
  DESKTOP: 1.0,
  WIDE_DESKTOP: 1.0,
  ULTRA_WIDE_DESKTOP: 1.0,
};

const resolveReelScale = (layout) => GAME_VIEW_REEL_SCALE_CONFIG[layout] ?? 1;

/**
 * Resolve the offset for a layout into { x, y } multipliers.
 * Accepts a plain number (y-only) or an { x, y } object.
 */
const resolveOffset = (layout) => {
  const entry = GAME_VIEW_OFFSET_CONFIG[layout];
  if (typeof entry === "number") return { x: 0, y: entry };
  return { x: entry?.x ?? 0, y: entry?.y ?? 0 };
};

/**
 * Where the scene goes on screen and at what scale: the ONE placement shared
 * by the spine (applyResponsiveScaling) and the reel set's no-spine backup in
 * GameViewSimple.jsx. `scale` is the spine's; `reelScale` is the reel-block
 * bone's, on top of it.
 */
export const resolveBoardPlacement = ({
  dimensions,
  symbolHeight,
  symbolScale,
  layout,
}) => {
  const offset = resolveOffset(layout);
  return {
    // Y is SUBTRACTED so a positive value pushes the scene up.
    x: dimensions.width / 2 + symbolHeight * offset.x,
    y: dimensions.height / 2 - symbolHeight * offset.y,
    scale: symbolScale * resolveZoom(layout),
    reelScale: resolveReelScale(layout),
  };
};

/**
 * GameViewSimpleController — the base game's scene spine (game_view).
 *
 * Owns the layout skins, scale and position on resize, the injection slots
 * for the reels and the symbol overlay (published to the SlotRegistry), the
 * logo spine (the stateful Pot), the jackpot plates' values and hits, the
 * anticipation spines and coin counter, the coin trail layer, and dormancy
 * while another room covers this one.
 *
 * Injection points are spine SLOTS, so the animator owns the layering. A
 * bone-following fallback exists for a skeleton that marks the spot with a
 * bare bone; see _attachToBone.
 */
class GameViewSimpleController {
  // Which spine this controller drives and where it publishes its two slots.
  // A room built on the SAME skeleton (FreeSpinsGameViewController) extends
  // this class and overrides only this block — the skins, idle, scaling and
  // injection are shared.
  static SCENE = {
    TAG,
    SKELETON: SPINE_ASSETS.SKELETON,
    ATLAS: SPINE_ASSETS.ATLAS,
    // Spine name for SpineAtlasUploader (the assets.js entry name).
    SPINE_NAME: "game_view",
    REEL_SET_KEY: "gameView.reelSet",
    OVERLAY_REEL_SET_KEY: "gameView.overlayReelSet",
    // Optional per-scene overrides. IDLE_ANIMATIONS replaces the contract's
    // idle candidates (a room whose export names its idle differently);
    // HAS_LOGO false skips the logo injection for a room with no logo slot.
    IDLE_ANIMATIONS: null,
    HAS_LOGO: true,
  };

  constructor(
    container,
    dimensions,
    symbolWidth,
    symbolHeight,
    symbolScale,
    device,
    orientation,
    layout,
    slotRegistry,
  ) {
    this.container = container;
    this.dimensions = dimensions;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.symbolScale = symbolScale;
    this.device = device;
    this.orientation = orientation;
    this.layout = layout;
    this.spine = null;

    this._registerSlot = slotRegistry?.registerSlot ?? null;
    this._unregisterSlot = slotRegistry?.unregisterSlot ?? null;

    this.reelSlotContainer = null;
    this.overlayReelSlotContainer = null;
    this._reelBone = null;
    this.logoSpine = null;
    this._anticipationBaseX = null;

    this._currentLayout = null;

    this.initializeSpine();
  }

  // ==========================================================================
  // INITIALIZATION
  // ==========================================================================

  initializeSpine() {
    this.spine = this.createSpine();
    if (!this.spine) return;

    this.spine.position.set(0, 0);
    this.spine.zIndex = 1;
    this.spine.visible = true;
    this.container.addChild(this.spine);

    this.applySkin(this.resolveLayoutSkin());
    this.playIdle();
    this.applyResponsiveScaling();
    this.applyReelBlockScale();
    this.attachSlotContainers();
    if (this.constructor.SCENE.HAS_LOGO !== false) this.attachLogo();
    this.attachAnticipation();
    this.attachReelAnticipation();
    // Built now, hidden, so the first anticipation does not stutter.
    this._warmAnticipations();
    // A room hidden from the start sleeps from the start (setDormant).
    this._syncDormancy();

    // Push the atlas pages to the GPU before the first frame that needs them,
    // so the scene does not hitch on its reveal. The anticipations' and the
    // coin counter's too, so their first showing does not upload them.
    requestAnimationFrame(() => {
      SpineAtlasUploader.upload(this.constructor.SCENE.SPINE_NAME);
      SpineAtlasUploader.upload("anticipation");
      SpineAtlasUploader.upload("reel_anticipation");
      SpineAtlasUploader.upload("basic_win");
    });
  }

  createSpine() {
    const { TAG: tag, SKELETON, ATLAS } = this.constructor.SCENE;
    try {
      const assets = PIXI.Assets.cache;
      if (!assets.has(SKELETON) || !assets.has(ATLAS)) {
        console.warn(`[${tag}] Spine assets not in cache:`, SKELETON, ATLAS);
        return null;
      }

      const spine = Spine.from({ skeleton: SKELETON, atlas: ATLAS });

      // skeleton and state are siblings on the Spine display object, not nested.
      if (!spine?.skeleton || !spine?.state || !spine.skeleton.data) {
        console.warn(`[${tag}] Spine created but incomplete`);
        return null;
      }

      return spine;
    } catch (error) {
      console.error(`[${tag}] Failed to create spine:`, error);
      return null;
    }
  }

  // ==========================================================================
  // NAME RESOLUTION — everything below reads the contract, never a literal
  // ==========================================================================

  _firstAnimation(candidates) {
    const data = this.spine?.skeleton?.data;
    if (!data) return null;
    for (const name of candidates) {
      if (data.findAnimation(name)) return name;
    }
    return null;
  }

  _firstSkin(candidates) {
    const data = this.spine?.skeleton?.data;
    if (!data) return null;
    for (const name of candidates) {
      if (data.findSkin(name)) return name;
    }
    return null;
  }

  _firstBone(candidates) {
    const skeleton = this.spine?.skeleton;
    if (!skeleton) return null;
    for (const name of candidates) {
      const bone = skeleton.findBone(name);
      if (bone) return bone;
    }
    return null;
  }

  _firstSlotName(candidates) {
    const skeleton = this.spine?.skeleton;
    if (!skeleton) return null;
    for (const name of candidates) {
      if (skeleton.findSlot(name)) return name;
    }
    return null;
  }

  /**
   * Draw-order index of a slot. Used to compute a zIndex that survives a
   * re-export, where absolute slot numbers change but the order does not.
   */
  _getSlotIndex(slotName) {
    const skeleton = this.spine?.skeleton;
    if (!skeleton || !slotName) return 0;
    const slot = skeleton.findSlot(slotName);
    return slot ? skeleton.slots.indexOf(slot) : 0;
  }

  // ==========================================================================
  // SKIN — one per orientation, combined with "default"
  // ==========================================================================

  /**
   * Apply the layout skin on top of "default".
   *
   * They are combined rather than swapped: "default" carries the art that does
   * not change with orientation, and the layout skin only re-positions. Setting
   * the layout skin alone would drop everything default holds.
   */
  applySkin(layout) {
    const data = this.spine?.skeleton?.data;
    if (!data) return;

    const defaultSkin = data.findSkin("default");
    if (!defaultSkin) {
      console.warn(
        `[${this.constructor.SCENE.TAG}] No default skin — cannot apply skins`,
      );
      return;
    }

    const combined = new Skin("combined");
    combined.addSkin(defaultSkin);

    const layoutSkin = layout ? data.findSkin(layout) : null;
    if (layoutSkin) combined.addSkin(layoutSkin);

    this.spine.skeleton.setSkin(combined);
    this.spine.skeleton.setSlotsToSetupPose();

    this._currentLayout = layout;
  }

  /** The layout skin name for the current device and orientation. */
  resolveLayoutSkin() {
    const isPortrait =
      this.device !== PixiSettings.DEVICE.DESKTOP &&
      this.orientation === PixiSettings.ORIENTATION.PORTRAIT;

    return this._firstSkin(
      isPortrait
        ? SPINE_CONTRACT.PORTRAIT_SKINS
        : SPINE_CONTRACT.LANDSCAPE_SKINS,
    );
  }

  setOrientation(orientation) {
    this.orientation = orientation;
    const layout = this.resolveLayoutSkin();
    if (layout === this._currentLayout) return;
    this.applySkin(layout);
    // New game view: the logo has a slot per layout — move it across.
    const logoSlot = this._logoSlotName();
    if (this.logoSpine && logoSlot) this.spine.addSlotObject(logoSlot, this.logoSpine);
  }

  /** The logo's slot: the layout's own (new game view), else the shared one. */
  _logoSlotName() {
    const isPortrait =
      this.device !== PixiSettings.DEVICE.DESKTOP &&
      this.orientation === PixiSettings.ORIENTATION.PORTRAIT;
    return (
      this._firstSlotName(
        isPortrait ? SPINE_CONTRACT.LOGO_SLOTS_PORTRAIT : SPINE_CONTRACT.LOGO_SLOTS_LANDSCAPE,
      ) || this._firstSlotName(SPINE_CONTRACT.LOGO_SLOTS)
    );
  }

  // ==========================================================================
  // IDLE — the scene's one looping clip, on track 0
  // ==========================================================================

  playIdle() {
    if (!this.spine?.state) return;
    const idle = this._firstAnimation(
      this.constructor.SCENE.IDLE_ANIMATIONS ?? SPINE_CONTRACT.IDLE_ANIMATIONS,
    );
    if (!idle) {
      console.warn(
        `[${this.constructor.SCENE.TAG}] No idle animation found in skeleton`,
      );
      return;
    }
    // Skip if already running, so a resize cannot restart it from frame 0.
    if (this.spine.state.getCurrent(0)?.animation?.name === idle) return;
    this.spine.state.setAnimation(0, idle, true);
  }

  // ==========================================================================
  // ACCENTS — clips played OVER the idle, on their own tracks
  //
  // Behaviour lives in Games/Shared/sceneAccents, shared by both rooms.
  // ==========================================================================

  // Off in this game: its scene spines have no accent clips, so every call
  // would only log "No accent animation found". Both play methods return
  // false, as for a missing clip; no caller uses the value.
  static ACCENTS_ENABLED = false;

  /** @see playSceneAccent */
  playAccent(candidates, options) {
    if (!this.constructor.ACCENTS_ENABLED) return false;
    return playSceneAccent(this.spine, candidates, {
      tag: this.constructor.SCENE.TAG,
      ...options,
    });
  }

  /** @see playSceneAccentForWindow */
  playAccentForWindow(candidates, windowMs, options) {
    if (!this.constructor.ACCENTS_ENABLED) return false;
    return playSceneAccentForWindow(this.spine, candidates, windowMs, {
      tag: this.constructor.SCENE.TAG,
      ...options,
    });
  }

  /** @see stopSceneAccents */
  stopAccent() {
    stopSceneAccents(this.spine);
  }

  // ==========================================================================
  // ANTICIPATION — one reel at a time, driven by ReelControlContext
  // ==========================================================================

  /**
   * The standalone anticipation spine (new exports), in the scene's
   * `attachment_anticipation` slot. Only when the scene does NOT carry the
   * clips itself — the older spines keep their built-in path.
   *
   * Wrapped in a holder: spine-pixi rewrites a slot object's own visible /
   * alpha every frame, so the spine inside is what gets shown and hidden.
   */
  attachAnticipation() {
    const tag = this.constructor.SCENE.TAG;
    const slotName = this._firstSlotName(SPINE_CONTRACT.ANTICIPATION_SLOTS);
    if (!slotName || this._firstAnimation(SPINE_CONTRACT.ANTICIPATION_LOOP)) return;

    const { SKELETON, ATLAS } = SPINE_CONTRACT.ANTICIPATION_SPINE;
    const assets = PIXI.Assets.cache;
    if (!assets.has(SKELETON) || !assets.has(ATLAS)) {
      console.warn(`[${tag}] Anticipation spine not in cache`);
      return;
    }
    try {
      const spine = Spine.from({ skeleton: SKELETON, atlas: ATLAS });
      spine.name = "anticipation";
      spine.visible = false;
      const holder = new PIXI.Container();
      holder.name = "anticipation_slot_content";
      holder.addChild(spine);
      this.spine.addSlotObject(slotName, holder);
      this.anticipationSpine = spine;
      this._anticipationSlotName = slotName;
    } catch (error) {
      console.error(`[${tag}] Failed to create anticipation spine:`, error);
    }
  }

  /**
   * Where the anticipation clips play: the standalone spine (track 0), or
   * the scene's own clips (ANTICIPATION_TRACK) on the older spines.
   */
  _anticipationHost() {
    if (this.anticipationSpine) return { spine: this.anticipationSpine, track: 0 };
    return { spine: this.spine, track: SPINE_CONTRACT.ANTICIPATION_TRACK };
  }

  _anticipationClip(host, candidates) {
    const data = host.spine?.skeleton?.data;
    return candidates.find((name) => data?.findAnimation(name)) || null;
  }

  /**
   * Move the anticipation bone over `reelIndex` and play in → loop there.
   * The chain calls it once per reel, after the previous reel's out.
   */
  playAnticipation(reelIndex) {
    if (this.anticipationSpine) {
      this._playStandaloneAnticipation(reelIndex);
      return;
    }
    const state = this.spine?.state;
    const bone = this._firstBone(SPINE_CONTRACT.ANTICIPATION_BONES);
    const inClip = this._firstAnimation(SPINE_CONTRACT.ANTICIPATION_IN);
    const loopClip = this._firstAnimation(SPINE_CONTRACT.ANTICIPATION_LOOP);
    if (!state || !bone || !loopClip) return;

    // The export places the bone over the middle reel; step it one reel
    // spacing (native spine units) per reel from there.
    if (this._anticipationBaseX === null) this._anticipationBaseX = bone.data.x;
    const middle = (Settings.REEL_COUNT - 1) / 2;
    const pitch = NATIVE_REELSET_SIZE.reelSpacing;
    bone.x = this._anticipationBaseX + (reelIndex - middle) * pitch;

    this._resetAnticipationSlots();
    const track = SPINE_CONTRACT.ANTICIPATION_TRACK;
    if (inClip) {
      state.setAnimation(track, inClip, false);
      state.addAnimation(track, loopClip, true, 0);
    } else {
      state.setAnimation(track, loopClip, true);
    }
    // `in`'s first frame now — no frame of the reset slots (see
    // _playStandaloneAnticipation).
    state.apply(this.spine.skeleton);
  }

  /**
   * The standalone spine over `reelIndex`: its slot bone goes to the reel
   * window's centre (the reel slot's bone — both are children of `reels`),
   * stepped one reel pitch per reel, then in → loop.
   */
  _playStandaloneAnticipation(reelIndex) {
    const host = this._anticipationHost();
    const state = host.spine.state;
    const skeleton = this.spine?.skeleton;
    const bone = skeleton?.findSlot(this._anticipationSlotName)?.bone;
    const anchor = skeleton?.findSlot(this._firstSlotName(SPINE_CONTRACT.REEL_SLOTS))?.bone;
    const inClip = this._anticipationClip(host, SPINE_CONTRACT.ANTICIPATION_IN);
    const loopClip = this._anticipationClip(host, SPINE_CONTRACT.ANTICIPATION_LOOP);
    if (!state || !bone || !loopClip) return;

    const middle = (Settings.REEL_COUNT - 1) / 2;
    const pitch = NATIVE_REELSET_SIZE.reelSpacing;
    bone.x = (anchor ? anchor.x : bone.data.x) + (reelIndex - middle) * pitch;
    bone.y = anchor ? anchor.y : bone.data.y;

    state.clearTracks();
    host.spine.skeleton.setToSetupPose();
    host.spine.visible = true;
    if (inClip) {
      state.setAnimation(host.track, inClip, false);
      state.addAnimation(host.track, loopClip, true, 0);
    } else {
      state.setAnimation(host.track, loopClip, true);
    }
    // ── `in`'s first frame NOW (2026-10-13: "feels like the animation starts
    // 2 times") ── setAnimation takes effect on the spine's next update; the
    // chain calls this from a ticker timer, often AFTER the spine updated this
    // frame, so one frame drew the bare setup pose (every flame on, full
    // strength) before `in` began from nothing. playReelAnticipation does the
    // same.
    state.update(0);
    state.apply(host.spine.skeleton);
  }

  /**
   * Play anticipation_out on the reel it is on, then clear the track and hide
   * the clips' slots. No-op when nothing is playing.
   */
  stopAnticipation() {
    if (this.anticipationSpine) {
      const host = this._anticipationHost();
      const state = host.spine.state;
      if (!state?.getCurrent(host.track)) return;
      const outClip = this._anticipationClip(host, SPINE_CONTRACT.ANTICIPATION_OUT);
      if (!outClip) {
        this.hideAnticipation();
        return;
      }
      const entry = state.setAnimation(host.track, outClip, false);
      entry.listener = {
        complete: () => {
          if (state.getCurrent(host.track) !== entry) return;
          this.hideAnticipation();
        },
      };
      return;
    }
    const state = this.spine?.state;
    const track = SPINE_CONTRACT.ANTICIPATION_TRACK;
    if (!state?.getCurrent(track)) return;

    const outClip = this._firstAnimation(SPINE_CONTRACT.ANTICIPATION_OUT);
    if (!outClip) {
      this.hideAnticipation();
      return;
    }
    const entry = state.setAnimation(track, outClip, false);
    entry.listener = {
      complete: () => {
        if (state.getCurrent(track) !== entry) return;
        this.hideAnticipation();
      },
    };
  }

  /**
   * The three clips' lengths in ms, for ReelControlContext to time the chain
   * on. Missing clips come back undefined and the caller falls back.
   */
  getAnticipationTiming() {
    const host = this._anticipationHost();
    const ms = (candidates) => {
      const name = this._anticipationClip(host, candidates);
      const animation = name
        ? host.spine?.skeleton?.data?.findAnimation(name)
        : null;
      return animation ? Math.round(animation.duration * 1000) : undefined;
    };
    return {
      inMs: ms(SPINE_CONTRACT.ANTICIPATION_IN),
      loopMs: ms(SPINE_CONTRACT.ANTICIPATION_LOOP),
      outMs: ms(SPINE_CONTRACT.ANTICIPATION_OUT),
    };
  }

  /**
   * The reel anticipation spine, in the scene's `attachment_reel_anticipation`
   * slot. Same holder as attachAnticipation: spine-pixi rewrites the slot
   * object's own visible every frame, so the spine inside is shown / hidden.
   * Its clips key its bone back to 0,0, so it plays centred on the slot.
   */
  attachReelAnticipation() {
    const tag = this.constructor.SCENE.TAG;
    const slotName = this._firstSlotName(SPINE_CONTRACT.REEL_ANTICIPATION_SLOTS);
    if (!slotName) return;

    const { SKELETON, ATLAS } = SPINE_CONTRACT.REEL_ANTICIPATION_SPINE;
    const assets = PIXI.Assets.cache;
    if (!assets.has(SKELETON) || !assets.has(ATLAS)) {
      console.warn(`[${tag}] Reel anticipation spine not in cache`);
      return;
    }
    try {
      const spine = Spine.from({ skeleton: SKELETON, atlas: ATLAS });
      spine.name = "reel_anticipation";
      spine.visible = false;
      const holder = new PIXI.Container();
      holder.name = "reel_anticipation_slot_content";
      holder.addChild(spine);
      this.spine.addSlotObject(slotName, holder);
      this.reelAnticipationSpine = spine;
    } catch (error) {
      console.error(`[${tag}] Failed to create reel anticipation spine:`, error);
    }
  }

  _reelAnticipationClip(candidates) {
    const data = this.reelAnticipationSpine?.skeleton?.data;
    return candidates.find((name) => data?.findAnimation(name)) || null;
  }

  /**
   * Builds, hidden, what the first anticipation would otherwise build in its
   * own frames (a visible stutter): the coin counter spine and text, where
   * the scene has its slot, and both anticipation spines' meshes, posed at the
   * end of `in` (all layers on), then reset. `visible` stays false.
   */
  _warmAnticipations() {
    if (this.spine?.skeleton?.findSlot(COIN_ANTICIPATION_COUNTER.SLOT)) {
      // this._coinCounterSpine();
      // ...and every label it can show, drawn now (0..6 of 6), not on the
      // frame a reel lands.
      const counter = this._coinCounterSpine();
      const total = PROVIDER_CONFIG.HOLD_AND_WIN_TRIGGER?.REQUIRED_COINS ?? 0;
      if (counter) {
        for (let n = 0; n <= total; n++) {
          const label = i18next.t(COIN_ANTICIPATION_COUNTER.KEY, { count: n, total }).toUpperCase();
          const text = this._coinCounterLabel(counter, label);
          // Upload its texture now, too.
          if (text.texture?.baseTexture) SpineAtlasUploader.uploadTexture?.(text.texture);
        }
      }
    }
    const standaloneIn = this.anticipationSpine
      ? this._anticipationClip(this._anticipationHost(), SPINE_CONTRACT.ANTICIPATION_IN)
      : null;
    const pairs = [
      [this.anticipationSpine, standaloneIn],
      [this.reelAnticipationSpine, this._reelAnticipationClip(SPINE_CONTRACT.REEL_ANTICIPATION_IN)],
    ];
    for (const [spine, clip] of pairs) {
      if (!spine?.state || !clip) continue;
      try {
        const anim = spine.skeleton.data.findAnimation(clip);
        spine.state.clearTracks();
        spine.skeleton.setToSetupPose();
        const entry = spine.state.setAnimation(0, clip, false);
        if (entry && anim) entry.trackTime = anim.duration;
        spine.state.apply(spine.skeleton);
        spine.skeleton.updateWorldTransform(spine.skeleton.physics !== undefined ? 2 : 0);
        spine.renderMeshes?.();
      } catch {
        // Warm-up is an optimisation — never let it stop the scene.
      } finally {
        spine.state.clearTracks();
        spine.skeleton.setToSetupPose();
      }
    }
    // Then EVERY clip's meshes (not only the end of `in`), the counter's too —
    // a few spines a frame, each left alone while it is on screen.
    const counterSpine = this._coinCounter?.spine;
    for (const spine of [this.anticipationSpine, this.reelAnticipationSpine, counterSpine]) {
      // The counter is always `visible` (hidden by alpha): busy = on screen.
      if (spine) {
        queueMeshWarm({
          spine,
          isBusy: () =>
            spine === counterSpine
              ? spine.alpha > COIN_ANTICIPATION_COUNTER.HIDDEN_ALPHA
              : spine.visible,
        });
      }
    }
  }

  /** The chain has started: in → loop. Already up = left as it is. */
  playReelAnticipation() {
    const spine = this.reelAnticipationSpine;
    const state = spine?.state;
    if (!state) return;
    const current = state.getCurrent(0)?.animation?.name;
    const outClip = this._reelAnticipationClip(SPINE_CONTRACT.REEL_ANTICIPATION_OUT);
    if (spine.visible && current && current !== outClip) return;

    const inClip = this._reelAnticipationClip(SPINE_CONTRACT.REEL_ANTICIPATION_IN);
    const loopClip = this._reelAnticipationClip(SPINE_CONTRACT.REEL_ANTICIPATION_LOOP);
    if (!loopClip) return;
    state.clearTracks();
    spine.skeleton.setToSetupPose();
    // The setup pose brings every part's art back; the hidden ones go again.
    for (const name of SPINE_CONTRACT.REEL_ANTICIPATION_HIDDEN_SLOTS) {
      spine.skeleton.findSlot(name)?.setAttachment(null);
    }
    spine.visible = true;
    // ── Its fire sound, on loop for as long as the frame is up (2026-10-20) ──
    // Stopped first: play() on a looping Howl still going adds a second copy.
    soundManager.stop(SOUND_NAME.anticipation_fire);
    soundManager.play(SOUND_NAME.anticipation_fire);
    if (inClip) {
      state.setAnimation(0, inClip, false);
      state.addAnimation(0, loopClip, true, 0);
    } else {
      state.setAnimation(0, loopClip, true);
    }
    // Apply `in`'s first frame now: setAnimation only takes effect on the
    // next update, and the setup pose in between has every layer attached and
    // opaque, which would flash.
    state.update(0);
    state.apply(spine.skeleton);
  }

  /** The chain has ended: out, then hidden. No-op when it is not up. */
  stopReelAnticipation() {
    const spine = this.reelAnticipationSpine;
    const state = spine?.state;
    if (!state?.getCurrent(0) || !spine.visible) return;
    const outClip = this._reelAnticipationClip(SPINE_CONTRACT.REEL_ANTICIPATION_OUT);
    if (!outClip) {
      this.hideReelAnticipation();
      return;
    }
    if (state.getCurrent(0).animation?.name === outClip) return;
    // The frame goes out: its fire sound with it.
    // soundManager.stop(SOUND_NAME.anticipation_fire);
    // ...fading over the out, not cut (2026-10-22: "finish nicely").
    const outMs = (spine.skeleton.data.findAnimation(outClip)?.duration ?? 0) * 1000;
    soundManager.fadeOut(SOUND_NAME.anticipation_fire, outMs || BG_DARKEN.MIX * 1000);
    const entry = state.setAnimation(0, outClip, false);
    entry.listener = {
      complete: () => {
        if (state.getCurrent(0) !== entry) return;
        this.hideReelAnticipation();
      },
    };
  }

  /** Instant: clear it and hide it (a new round, a room change). */
  hideReelAnticipation() {
    // Cleared (a new round, a room change, no out clip): the sound goes too.
    soundManager.stop(SOUND_NAME.anticipation_fire);
    const spine = this.reelAnticipationSpine;
    if (!spine) return;
    spine.state?.clearTracks();
    spine.skeleton?.setToSetupPose();
    spine.visible = false;
  }

  /** Instant: clear the track and hide every anticipation slot. */
  hideAnticipation() {
    if (this.anticipationSpine) {
      const spine = this.anticipationSpine;
      spine.state?.clearTracks();
      spine.skeleton?.setToSetupPose();
      spine.visible = false;
      return;
    }
    const state = this.spine?.state;
    if (!state) return;
    state.clearTrack(SPINE_CONTRACT.ANTICIPATION_TRACK);
    this._resetAnticipationSlots();
  }

  /**
   * Put the clips' slots back to setup — in the export none of them has an
   * attachment there, so this hides them. Needed because a clip cut short
   * (clearTrack, or `in` replacing `loop`) leaves its last attachments on.
   */
  _resetAnticipationSlots() {
    const prefix = SPINE_CONTRACT.ANTICIPATION_SLOT_PREFIX;
    for (const slot of this.spine?.skeleton?.slots ?? []) {
      if (slot.data.name.startsWith(prefix)) slot.setToSetupPose();
    }
  }

  // ==========================================================================
  // SCALING
  // ==========================================================================

  applyResponsiveScaling() {
    if (!this.dimensions || !this.spine) return;

    // The same placement the reel set's no-spine backup uses.
    const board = resolveBoardPlacement({
      dimensions: this.dimensions,
      symbolHeight: this.symbolHeight,
      symbolScale: this.symbolScale,
      layout: this.layout,
    });
    this.spine.scale.set(board.scale);
    this.spine.position.set(board.x, board.y);
    this.coverBackground();
    this.applyColumnOffset();
  }

  /**
   * ── One world-transform hook, many users (2026-10-16) ── spine-pixi has ONE
   * beforeUpdateWorldTransforms / afterUpdateWorldTransforms slot each, and
   * every user that assigned it replaced the one before (the reel box's bone
   * sync replaced the jackpot column's). Users register under a key; the slot
   * runs them all, in order. Same key = replaces that user's own.
   */
  _onWorldHook(when, key, fn) {
    if (!this.spine) return;
    const field = when === "before" ? "_beforeWorldHooks" : "_afterWorldHooks";
    if (!this[field]) this[field] = new Map();
    this[field].set(key, fn);
    const hooks = this[field];
    const run = (s) => {
      for (const hook of hooks.values()) hook(s);
    };
    if (when === "before") this.spine.beforeUpdateWorldTransforms = run;
    else this.spine.afterUpdateWorldTransforms = run;
  }

  /**
   * The landscape jackpot column's per-layout offset
   * (LANDSCAPE_COLUMN_OFFSET_CONFIG). Added to the column bones each frame
   * after the animation is applied and taken off again once the world
   * transforms are built, so it never builds up and the spine data is never
   * changed. The logo's Random Pot flight to the reels (logo_reel_landscape)
   * lets go of it as it travels, so it still lands on the reels exactly.
   */
  applyColumnOffset() {
    const spine = this.spine;
    if (!spine?.skeleton) return;
    if (this.constructor.SCENE.TAG === "HoldAndWinGameView") return;
    this._columnOffsetY =
      (LANDSCAPE_COLUMN_OFFSET_CONFIG[this.layout]?.y ?? 0) * Settings.SYMBOL_HEIGHT;
    if (this._columnHooked) return;
    this._columnHooked = true;

    const skeleton = spine.skeleton;
    const bones = LANDSCAPE_COLUMN_BONES.map((n) => skeleton.findBone(n)).filter(Boolean);
    const logo = skeleton.findBone(LANDSCAPE_LOGO_BONE);
    if (this.constructor === GameViewSimpleController) {
      baseColumnSetup = Object.fromEntries(
        bones.map((b) => [
          b.data.name,
          { x: b.data.x, y: b.data.y, scaleX: b.data.scaleX, scaleY: b.data.scaleY },
        ]),
      );
    }
    // How far the flight moves the logo down to the reels (its biggest y key).
    let flightY = 0;
    const flight = skeleton.data.findAnimation(LANDSCAPE_LOGO_FLIGHT);
    const track = flight?.timelines.find(
      (t) => t instanceof TranslateTimeline && t.boneIndex === logo?.data.index,
    );
    if (track) {
      const step = track.getFrameEntries();
      for (let i = 0; i < track.frames.length; i += step) {
        if (Math.abs(track.frames[i + 2]) > Math.abs(flightY)) flightY = track.frames[i + 2];
      }
    }

    const restore = new Map();
    // spine.beforeUpdateWorldTransforms = () => {
    this._onWorldHook("before", "column", () => {
      restore.clear();
      const dy = this._columnOffsetY || 0;
      if (!dy) return;
      for (const bone of bones) {
        restore.set(bone, bone.y);
        let share = 1;
        if (bone === logo && flightY) {
          const travelled = (bone.y - bone.data.y) / flightY; // 0 at rest, 1 on the reels
          share = 1 - Math.min(1, Math.max(0, travelled));
        }
        bone.y += dy * share;
      }
    });
    // spine.afterUpdateWorldTransforms = () => {
    this._onWorldHook("after", "column", () => {
      for (const [bone, y] of restore) bone.y = y;
    });
  }

  /**
   * ── The background always covers the window (2026-10-15, as Mad Labs
   * sized its background apart from the reels) ── more padding shrinks the
   * whole room, background too, and on a short wide window it no longer
   * reached the edges. The background ART (bones bg_1 / bg_2 under
   * `bg_master`, the `land_bg_bg` slots) grows about bg_master's origin just
   * enough to reach the window's left and right edges — never the palms or
   * smoke on bg_master, never shrunk, so a room that already covers is drawn
   * exactly as before. Called after the room is placed
   * (applyResponsiveScaling). No `bg_master` / art: nothing.
   */
  coverBackground() {
    // The BASE room only (2026-10-15: the H&W room's background "was good and
    // needed no further tweaking"; the free-spins room's untouched as well).
    // if (this.constructor !== GameViewSimpleController) return;
    // + the Hold & Win room (2026-10-22): its background ART only (bg_1 /
    // bg_2 — never bg_master's palms), out to the edges, no cap, nothing
    // published (the bar and the room sizes follow the BASE view).
    const isBase = this.constructor === GameViewSimpleController;
    const isHoldAndWin = this.constructor.SCENE?.TAG === "HoldAndWinGameView";
    if (!isBase && !isHoldAndWin) return;
    const spine = this.spine;
    const skeleton = spine?.skeleton;
    const bone = skeleton?.findBone("bg_master");
    const width = this.dimensions?.width;
    const height = this.dimensions?.height;
    if (!bone || !width || !height) return;
    // bone.scaleX = bone.data.scaleX;
    // bone.scaleY = bone.data.scaleY;
    // ── The ART bones only (2026-10-15) ── scaling bg_master also grew the
    // palms and smoke hanging off it (the H&W room's, in portrait: big dark
    // shapes around the play button). bg_1 / bg_2 carry the background image
    // (and the base room's vignette), at bg_master's origin, never animated.
    bone.scaleX = bone.data.scaleX;
    bone.scaleY = bone.data.scaleY;
    const artBones = ["bg_1", "bg_2"].map((n) => skeleton.findBone(n)).filter(Boolean);
    for (const art of artBones) {
      art.scaleX = art.data.scaleX;
      art.scaleY = art.data.scaleY;
    }
    // The art's bounds about the bone's origin, at its own scale — measured
    // once (the bone is never animated; its art does not change).
    // if (this._bgBounds === undefined) {
    //   this._bgBounds = null;
    // Measured until it works — an empty measurement is not kept (the room
    // is built before its idle clip has set the art on the slots).
    if (!this._bgBounds) {
      try {
        skeleton.updateWorldTransform(Physics.pose);
      } catch (e) {
        return;
      }
      const vertices = new Float32Array(8);
      let minX = Infinity;
      let maxX = -Infinity;
      let minY = Infinity;
      let maxY = -Infinity;
      // The art on the slot, else the skin's art for it (not set yet at build).
      const skinned = skeleton.skin?.getAttachments?.() || [];
      for (const name of ["land_bg_bg", "land_bg_bg2"]) {
        const slot = skeleton.findSlot(name);
        if (!slot) continue;
        // const attachment = slot?.getAttachment();
        const attachment =
          (slot.getAttachment()?.computeWorldVertices && slot.getAttachment()) ||
          skinned.find(
            (e) => e.slotIndex === slot.data.index && e.attachment?.computeWorldVertices,
          )?.attachment;
        if (!attachment?.computeWorldVertices) continue;
        attachment.computeWorldVertices(slot, vertices, 0, 2);
        for (let i = 0; i < 8; i += 2) {
          minX = Math.min(minX, vertices[i] - bone.worldX);
          maxX = Math.max(maxX, vertices[i] - bone.worldX);
          minY = Math.min(minY, vertices[i + 1] - bone.worldY);
          maxY = Math.max(maxY, vertices[i + 1] - bone.worldY);
        }
      }
      if (minX < maxX && minY < maxY) this._bgBounds = { minX, maxX, minY, maxY };
    }
    const b = this._bgBounds;
    if (!b) return;
    // The bone's origin on screen and the room's pixels per spine unit
    // (skeleton y runs down, as the screen's).
    const k = spine.scale.x;
    const ox = spine.position.x + (bone.worldX - skeleton.x) * k;
    // const oy = spine.position.y + (bone.worldY - skeleton.y) * k; (sideways only)
    let grow = 1;
    if (b.minX < 0) grow = Math.max(grow, -ox / (b.minX * k));
    if (b.maxX > 0) grow = Math.max(grow, (width - ox) / (b.maxX * k));
    // if (b.minY < 0) grow = Math.max(grow, -oy / (b.minY * k));
    // if (b.maxY > 0) grow = Math.max(grow, (height - oy) / (b.maxY * k));
    // Sideways only (2026-10-15: "only to fill the gaps") — the gaps are at
    // the left and right; above the art the clouds and sky are other layers,
    // and filling that with the art blew the base room's up to 1.9x.
    if (grow > 1) grow *= 1.005; // a hair past the edge, never a seam
    // ── No further than the art goes (Vladimir, 2026-10-22) ── the grow had no
    // limit, so on an ultra-wide window the background stretched across the
    // whole screen ("stretching infinitely") while the board and UI stayed in
    // the middle. It stops at the art's own size now, as 3 Orbs of Zeus.
    if (isHoldAndWin) {
      for (const art of artBones) {
        art.scaleX = art.data.scaleX * grow;
        art.scaleY = art.data.scaleY * grow;
      }
      return;
    }
    grow = Math.min(grow, BG_COVER_MAX_GROW);
    // bone.scaleX = bone.data.scaleX * grow;
    // bone.scaleY = bone.data.scaleY * grow;
    // for (const art of artBones) {
    //   art.scaleX = art.data.scaleX * grow;
    //   art.scaleY = art.data.scaleY * grow;
    // }
    // ── The WHOLE background grows, not only the landscape (2026-10-16) ──
    // the pyramids are in the landscape art (bg_1 / bg_2); the sky, clouds,
    // sun and smoke are other layers under bg_master. Growing only the art
    // made the pyramids outgrow the sky on a wide window. bg_master carries
    // them all (base room only — see the top of this method).
    bone.scaleX = bone.data.scaleX * grow;
    bone.scaleY = bone.data.scaleY * grow;
    // Its width on screen — the bottom bar stays inside it (utils/gameViewArtWidth).
    publishGameViewArtWidth((b.maxX - b.minX) * k * grow);
  }

  resize(
    dimensions,
    symbolWidth,
    symbolHeight,
    symbolScale,
    device,
    orientation,
    layout,
  ) {
    this.dimensions = dimensions;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.symbolScale = symbolScale;
    this.device = device;
    this.layout = layout;

    this.applyResponsiveScaling();
    this.setOrientation(orientation);
    this.applyReelBlockScale();
  }

  /**
   * Scale the reel block inside the scene.
   *
   * Safe to set once and leave: the idle animation does not key the `reels`
   * bone, and spine only writes bones an animation actually touches — so the
   * value is not overwritten every frame. It is re-applied on resize anyway,
   * because the layout (and therefore the value) can change.
   *
   * The injection point is a CHILD of this bone, so the reels scale with the
   * frame automatically and the two can never drift apart.
   */
  applyReelBlockScale() {
    const bone = this._firstBone(SPINE_CONTRACT.REEL_BLOCK_BONES);
    if (!bone) return;
    const scale = resolveReelScale(this.layout);
    bone.scaleX = scale;
    bone.scaleY = scale;
  }

  // ==========================================================================
  // INJECTION — reels, symbol overlay, coin trail layer, logo
  // ==========================================================================

  attachSlotContainers() {
    if (!this.spine?.skeleton) return;

    // Reels first, so they are behind the overlay if both land on the same slot.
    this.reelSlotContainer = this._attachContainer({
      name: "gameview_reel_slot_content",
      registryKey: this.constructor.SCENE.REEL_SET_KEY,
      slotNames: SPINE_CONTRACT.REEL_SLOTS,
      boneNames: SPINE_CONTRACT.REEL_BONES,
      behindSlotNames: SPINE_CONTRACT.REEL_BEHIND_SLOTS,
    });

    this.overlayReelSlotContainer = this._attachContainer({
      name: "gameview_overlay_reel_slot_content",
      registryKey: this.constructor.SCENE.OVERLAY_REEL_SET_KEY,
      slotNames: SPINE_CONTRACT.OVERLAY_SLOTS,
    });

    // The coins' fire trails fly in this layer. `coin_trail_placeholder`
    // (hand-added, on an identity bone at the root) sits just before
    // `logo_placeholder`: above the reels, overlay and jackpot plates, below
    // only the logo, so a trail slips behind the logo as it lands.
    const trailSlot = this._firstSlotName(["coin_trail_placeholder"]);
    if (trailSlot) {
      const layer = new PIXI.Container();
      layer.name = "coin_trail_layer";
      layer.sortableChildren = true;
      this.spine.addSlotObject(trailSlot, layer);
      this.coinTrailContainer = layer;
    }
  }

  /**
   * Puts the logo spine into the scene's logo slot and loops the Pot's
   * current level clip, with the fire flipbook on its own track.
   *
   * A slot object, so spine copies the slot bone's transform onto it every
   * frame: the layout skins move and scale the logo with no code here.
   */
  attachLogo() {
    const tag = this.constructor.SCENE.TAG;
    const slotName = this._logoSlotName();
    if (!slotName) {
      console.warn(`[${tag}] No logo slot`, SPINE_CONTRACT.LOGO_SLOTS);
      return;
    }

    const assets = PIXI.Assets.cache;
    if (!assets.has(LOGO_ASSETS.SKELETON) || !assets.has(LOGO_ASSETS.ATLAS)) {
      console.warn(`[${tag}] Logo spine not in cache`);
      return;
    }

    try {
      const logo = Spine.from({
        skeleton: LOGO_ASSETS.SKELETON,
        atlas: LOGO_ASSETS.ATLAS,
      });
      logo.name = "logo";
      // Every change of clip on the logo blends by default...
      logo.state.data.defaultMix = LOGO_ASSETS.LEVEL_MIX;
      // ...except between level clips, which CUT (mix 0) at the clip's end:
      // each _in starts on the stage below's pose and ends on its own loop's,
      // so they meet pose to pose, and a blend would eat the end of the rise.
      // A change that is not a rise blends on its own entry (_playLogoRise).
      const levelClips = logo.skeleton.data.animations
        .map((animation) => animation.name)
        .filter((name) => name.startsWith("logo_lvl"));
      levelClips.forEach((from) =>
        levelClips.forEach((to) => logo.state.data.setMix(from, to, 0)),
      );
      // The flames, on their own track for good (prepareLogoFireLoop).
      if (prepareLogoFireLoop(logo.skeleton.data)) {
        logo.state.setAnimation(LOGO_ASSETS.FIRE_LOOP_TRACK, LOGO_ASSETS.FIRE_LOOP, true);
      }

      const firstAnimation = (candidates) =>
        candidates.find((name) => logo.skeleton.data.findAnimation(name));

      // The Pot's CURRENT stage (logoPot, restored from localStorage), without
      // its `_in`: the room opens on it already burning.
      const level =
        this._logoLevelClip(logo, logoPotStage()) ??
        firstAnimation(LOGO_ASSETS.LEVEL_ANIMATIONS);
      if (level) logo.state.setAnimation(LOGO_ASSETS.LEVEL_TRACK, level, true);
      this._logoStage = Math.min(logoPotStage(), LOGO_POT_STAGES);
      // Every stage change from here on — this room and the other share it.
      this._unsubscribeLogoPot?.();
      this._unsubscribeLogoPot = onLogoPotChange((stage, animate) =>
        this.setLogoStage(stage, animate),
      );

      this.spine.addSlotObject(slotName, logo);
      this.logoSpine = logo;
    } catch (error) {
      console.error(`[${tag}] Failed to create logo spine:`, error);
    }
  }

  /**
   * Create a container, put it in the spine at the right place, and publish it
   * to the SlotRegistry so the React side can inject into it.
   *
   * Two placement mechanics, and they are not interchangeable:
   *
   *   SLOT (preferred) — spine's addSlotObject syncs the transform AND sets
   *     zIndex from the slot's draw-order position every frame. The artist
   *     decides the layering; we inherit it and have nothing to tune.
   *
   *   BONE (fallback) — for a skeleton that marks the spot with a bare bone.
   *     The transform is copied by hand and the zIndex is forced to sit just
   *     after a named slot, because a bone carries no draw-order information.
   *
   * @returns {PIXI.Container|null}
   */
  _attachContainer({
    name,
    registryKey,
    slotNames,
    boneNames,
    behindSlotNames,
  }) {
    const container = new PIXI.Container();
    container.name = name;
    container.sortableChildren = true;

    const slotName = slotNames ? this._firstSlotName(slotNames) : null;

    if (slotName) {
      this.spine.addSlotObject(slotName, container);
    } else if (boneNames) {
      const bone = this._firstBone(boneNames);
      if (!bone) {
        console.warn(`[${this.constructor.SCENE.TAG}] No slot or bone found for ${name}`, {
          slotNames,
          boneNames,
        });
        return null;
      }
      this._attachToBone(container, bone, behindSlotNames);
    } else {
      console.warn(
        `[${this.constructor.SCENE.TAG}] Slot not found for ${name}`,
        slotNames,
      );
      return null;
    }

    if (
      this._registerSlot &&
      FEATURES_CONFIG.SLOT_REGISTRY.GAME_VIEW_INJECTION
    ) {
      this._registerSlot(registryKey, container);
    }

    return container;
  }

  /**
   * Fallback placement: add as a plain child and copy a bone's transform every
   * frame, with a forced zIndex.
   */
  _attachToBone(container, bone, behindSlotNames) {
    const behindSlot = behindSlotNames
      ? this._firstSlotName(behindSlotNames)
      : null;

    // +0.5 lands between two integer slot indices, so it never collides with a
    // real slot's own draw position.
    container.zIndex = behindSlot
      ? this._getSlotIndex(behindSlot) + 0.5
      : this.spine.skeleton.slots.length;

    this.spine.addChild(container);

    this._reelBone = bone;
    // this.spine.afterUpdateWorldTransforms = () => {
    //   this._syncBoneToContainer(this._reelBone, container);
    // };
    // Registered, not assigned — see _onWorldHook.
    this._onWorldHook("after", "reelBone", () => {
      this._syncBoneToContainer(this._reelBone, container);
    });
  }

  /**
   * Copy a bone's world transform onto a container.
   *
   * The same thing spine-pixi's own updateSlotObject does, minus the zIndex
   * override — which is the entire point, because the forced zIndex is what
   * keeps the reels under the frame.
   */
  _syncBoneToContainer(bone, container) {
    if (!bone || !container || container.destroyed) return;
    const transform = container.localTransform;
    transform.a = bone.a;
    transform.b = bone.c;
    transform.c = -bone.b;
    transform.d = -bone.d;
    transform.tx = bone.worldX;
    transform.ty = bone.worldY;
    container.transform.setFromMatrix(transform);
  }

  // ==========================================================================
  // LOGO TARGET, COIN COUNTER, JACKPOT PLATES, LOGO STAGES
  // ==========================================================================

  /**
   * Where a coin's trail lands: dead centre of the logo across (its origin —
   * the logo is centred on it), at the height of the FIRE on top (its fire
   * bone, live — it follows the logo in both layouts), in GLOBAL space. The
   * logo's centre if the bone is missing. Null without a logo.
   */
  getLogoTarget() {
    const logo = this.logoSpine;
    if (!logo || logo.destroyed || !logo.parent) return null;
    const skeleton = logo.skeleton;
    const bone = LOGO_ASSETS.FIRE_BONES.map((name) => skeleton?.findBone(name)).find(
      Boolean,
    );
    return logo.toGlobal(new PIXI.Point(0, bone ? bone.worldY : 0));
  }

  /**
   * Shows / updates the coin anticipation counter ("4 / 6 COINS"), called
   * by the anticipation chain (anticipationRunner) as each relevant reel
   * stops.
   *
   * The text sits in the injected win text spine's own text slot, so that
   * spine's clips drive it: `in` (fade + bounce) as it appears, then its hold
   * clip until it goes; an update only changes the number. A room whose
   * spine has no counter slot shows nothing.
   * @param {number} count coins landed so far
   * @param {number} total coins the trigger needs (6)
   */
  showCoinCounter(count, total) {
    const { SLOT, KEY } = COIN_ANTICIPATION_COUNTER;
    if (!this.spine?.skeleton?.findSlot(SLOT)) return;
    const counter = this._coinCounterSpine();
    if (!counter) return;
    // const { spine, text } = counter;
    const { spine } = counter;

    const shown = Math.min(count, total);
    // const label = i18next.t(KEY, { count: shown, total }).toUpperCase();
    // if (text.text !== label) text.text = label;
    // // Fit to MAX_WIDTH, then undo the spine's SPINE_SCALE.
    // const counterScale = 1 / (COIN_ANTICIPATION_COUNTER.SPINE_SCALE || 1);
    // text.scale.set(1);
    // const fit =
    //   text.width > COIN_ANTICIPATION_COUNTER.MAX_WIDTH
    //     ? COIN_ANTICIPATION_COUNTER.MAX_WIDTH / text.width
    //     : 1;
    // text.scale.set(fit * counterScale);
    // ── Swapped, not redrawn (2026-10-13: "still jittery") ── every count
    // redrew the gradient-stroke text and uploaded it on the frame a reel
    // lands. Each label is its own text, drawn once (at the warm-up); a count
    // shows its label and hides the rest.
    const label = i18next.t(KEY, { count: shown, total }).toUpperCase();
    const labelText = this._coinCounterLabel(counter, label);
    for (const t of counter.labels.values()) t.visible = t === labelText;

    // `in` once as it appears, then the hold clip until it goes; an update
    // only changes the number, never replays the clip.
    // const appearing = !counter.shown;
    // ── Decided by the SCREEN, not the flag (Vladimir, 2026-10-12: "I saw
    // it starting twice … updating text must not affect animation") ── a
    // count arriving while the counter is still on screen in its `out` took
    // the flag for "gone" and replayed the whole `in`. Now `in` plays only
    // when the counter is really off screen; caught mid-`out`, it goes back
    // to its hold clip from where it is (RESUME_MIX), no entrance.
    const current = spine.state.getCurrent(0);
    const onScreen = spine.alpha > COIN_ANTICIPATION_COUNTER.HIDDEN_ALPHA && !!current;
    const appearing = !counter.shown && !onScreen;
    if (!counter.shown && onScreen) {
      counter.shown = true;
      spine.state.clearListeners();
      // `out` fades the fire and the hold clip never keys it back: its
      // colours return with it (as `in` does below).
      for (const slot of spine.skeleton.slots) {
        if (slot.color && slot.data?.color) slot.color.setFromColor(slot.data.color);
      }
      const hold = spine.skeleton.data.findAnimation("idle") ? "idle" : "loop";
      if (current.animation?.name !== hold) {
        const entry = spine.state.setAnimation(0, hold, true);
        entry.mixDuration = COIN_ANTICIPATION_COUNTER.RESUME_MIX;
      } else {
        // Still in its hold clip with the `out` only QUEUED behind the cycle
        // (hideCoinCounter): drop the queued out, keep looping.
        spine.state.clearNext(current);
      }
    }
    counter.count = shown;
    if (appearing) {
      counter.shown = true;
      // ── Posed hidden, SHOWN two ticks later (as the win text does) ── the
      // number is a slot object, placed by the spine's bones only when the
      // spine renders; shown at once, its first frame drew it where the
      // spine had left it, then snapped.
      spine.alpha = COIN_ANTICIPATION_COUNTER.HIDDEN_ALPHA;
      const token = (counter.showToken = (counter.showToken || 0) + 1);
      // Counted, not nested addOnce: one added inside a tick can run in that
      // same tick.
      let ticks = 0;
      const reveal = () => {
        ticks += 1;
        if (ticks < 2) return;
        PIXI.Ticker.shared.remove(reveal);
        if (counter.shown && counter.showToken === token && !spine.destroyed) spine.alpha = 1;
      };
      PIXI.Ticker.shared.add(reveal);
      spine.state.clearListeners();
      // ── Every appearance starts CLEAN, like the first ── the last `out`
      // stays on the track holding its end pose, and `in` set over it would
      // MIX from that pose. The setup pose also brings back the colours `out`
      // faded (no clip keys them back).
      spine.state.clearTracks();
      spine.skeleton.setToSetupPose();
      spine.state.setAnimation(0, "in", false);
      // The hold clip: `loop` (basic_win) or `idle` (older winline_text).
      spine.state.addAnimation(
        0,
        spine.skeleton.data.findAnimation("idle") ? "idle" : "loop",
        true,
        0,
      );
      // `in`'s first frame now, bones included: apply() alone leaves the
      // world transforms at the setup pose until the spine's own update — a
      // first frame of full-size fire and text, then `in` from nothing.
      spine.state.update(0);
      spine.state.apply(spine.skeleton);
      spine.skeleton.updateWorldTransform(spine.skeleton.physics !== undefined ? 2 : 0);
    }
  }

  /** The counter's text for `label`, drawn once, fitted, kept hidden. */
  _coinCounterLabel(counter, label) {
    let text = counter.labels.get(label);
    if (text && !text.destroyed) return text;
    text = createWinNumberText(label, COIN_ANTICIPATION_COUNTER.FONT_SIZE);
    text.anchor.set(0.5);
    text.resolution = 2;
    text.roundPixels = false;
    // Fit to MAX_WIDTH, then undo the spine's SPINE_SCALE.
    const counterScale = 1 / (COIN_ANTICIPATION_COUNTER.SPINE_SCALE || 1);
    const fit =
      text.width > COIN_ANTICIPATION_COUNTER.MAX_WIDTH
        ? COIN_ANTICIPATION_COUNTER.MAX_WIDTH / text.width
        : 1;
    text.scale.set(fit * counterScale);
    text.visible = false;
    counter.holder.addChild(text);
    counter.labels.set(label, text);
    return text;
  }

  /** The counter goes — its `out`, or at once (`instant`, a new round). */
  hideCoinCounter(instant = false) {
    const counter = this._coinCounter;
    // if (!counter?.shown || !counter.spine?.state) return;
    if (!counter?.spine?.state) return;
    // ── An instant hide ALWAYS clears (2026-10-13: "the counter fire is not
    // set up properly") ── a chain's soft hide leaves the counter on screen
    // (shown false) for up to a loop + its `out`; the next round's instant
    // hide returned on `shown` and left it there, and that round's counter
    // RESUMED it — with the fire `out` had already faded, never keyed back.
    if (instant) {
      counter.shown = false;
      counter.count = null;
      counter.spine.state.clearListeners();
      counter.spine.state.clearTracks();
      counter.spine.alpha = COIN_ANTICIPATION_COUNTER.HIDDEN_ALPHA;
      return;
    }
    if (!counter.shown) return;
    counter.shown = false;
    counter.count = null;
    const { spine } = counter;
    spine.state.clearListeners();
    // `out` from where the current clip ends, not mid-loop (a jitter): queued
    // after the current loop cycle (addAnimation on a looping entry waits for
    // its loop end), or straight after `in` if it is still entering (its
    // queued hold clip dropped first).
    const current = spine.state.getCurrent(0);
    if (current && !current.loop && current.animation?.name !== "out") {
      spine.state.clearNext(current);
    }
    if (current && current.animation?.name !== "out") {
      spine.state.addAnimation(0, "out", false, 0);
    } else {
      spine.state.setAnimation(0, "out", false);
    }
    spine.state.addListener({
      complete: (entry) => {
        if (entry.animation?.name === "out" && !counter.shown) {
          spine.alpha = COIN_ANTICIPATION_COUNTER.HIDDEN_ALPHA;
        }
      },
    });
  }

  /** The injected win-text spine and its text, built on first use. */
  _coinCounterSpine() {
    const existing = this._coinCounter;
    if (existing?.spine && !existing.spine.destroyed && existing.spine.state) return existing;
    try {
      // The win text spine (`basic_win`), `fire` skin.
      // ── Its own copy, `coin_counter` ── basic_win with the text growing and
      // fading in WITH the fire in `in` (no 1.2 pop) and a gentler loop pulse;
      // skeleton only, on basic_win's atlas. basic_win if it is absent.
      const skeletonKey = PIXI.Assets.get("coin_counter-skeleton")
        ? "coin_counter-skeleton"
        : "basic_win-skeleton";
      const skeletonData = PIXI.Assets.get(skeletonKey);
      if (!skeletonData) return null;
      // Without its atlas it is built textureless (as WinTextController.
      // _createTexturelessSpine): the skeleton has no attachments, so the
      // empty atlas is never asked.
      const spine = PIXI.Assets.cache.has("basic_win-atlas")
        ? Spine.from({ skeleton: skeletonKey, atlas: "basic_win-atlas" })
        : new Spine(
            new SkeletonJson(new AtlasAttachmentLoader(new TextureAtlas(""))).readSkeletonData(
              skeletonData,
            ),
          );
      if (!spine?.state) return null;
      if (spine.skeleton.data.findSkin("fire")) {
        spine.skeleton.setSkinByName("fire");
        spine.skeleton.setSlotsToSetupPose();
      }
      spine.name = "coin_anticipation_counter";
      // ── RULE #1: mounted and rendered, hidden by ALPHA ── a `visible =
      // false` spine is never drawn, so its meshes and textures were built on
      // the frame it first appeared: the blink.
      spine.alpha = COIN_ANTICIPATION_COUNTER.HIDDEN_ALPHA;
      // The fire bigger (the text is counter-scaled in showCoinCounter). A
      // child of slotHolder below, so this scale is not rewritten.
      spine.scale.set(COIN_ANTICIPATION_COUNTER.SPINE_SCALE || 1);

      const holder = new PIXI.Container();
      holder.name = "coin_anticipation_counter_text";
      // const text = new PIXI.Text(
      //   "",
      //   new PIXI.TextStyle(
      //     scaleStyle(FONT_STYLES.jackpots, COIN_ANTICIPATION_COUNTER.FONT_SIZE),
      //   ),
      // );
      // The board's win number look — same fire behind it (2026-10-12).
      const text = createWinNumberText("", COIN_ANTICIPATION_COUNTER.FONT_SIZE);
      text.anchor.set(0.5);
      text.resolution = 2;
      text.roundPixels = false;
      holder.addChild(text);
      // The win text's own slot: its bone's scale and its slot's alpha carry
      // the text through in / idle / out.
      spine.addSlotObject(COIN_ANTICIPATION_COUNTER.TEXT_SLOT, holder);

      const slotHolder = new PIXI.Container();
      slotHolder.name = "coin_anticipation_counter_slot";
      slotHolder.addChild(spine);
      this.spine.addSlotObject(COIN_ANTICIPATION_COUNTER.SLOT, slotHolder);

      // this._coinCounter = { spine, text, shown: false, count: null };
      this._coinCounter = { spine, text, holder, labels: new Map(), shown: false, count: null };
      // Built on demand: asleep with its room if the room is hidden.
      this._syncDormancy();
      return this._coinCounter;
    } catch (error) {
      return null;
    }
  }

  /**
   * The jackpot plates' values — money text in each `txt_slot_jackpot_<tier>`
   * slot (hand-added over the plates' numbers), built on first use like the
   * coin counter. Slot objects, so the plates' bones place and scale them in
   * both layouts. Never pixel-rounded: the spine scales them.
   *
   * @param {Object<string, number>} values tier → money (subunits)
   */
  setJackpotValues(values, multipliers = null) {
    if (!this.spine?.skeleton || !values) return;
    if (!this._jackpotTexts) this._jackpotTexts = {};
    for (const [tier, value] of Object.entries(values)) {
      // txt_slot_jckpt_<tier> (current exports) or txt_slot_jackpot_<tier> (older).
      const slotName = this._firstSlotName([
        `txt_slot_jckpt_${tier}`,
        `txt_slot_jackpot_${tier}`,
      ]);
      if (!slotName) continue;

      const plate = JACKPOT_PLATE_TEXT_BY_TIER[tier] ?? JACKPOT_PLATE_TEXT;
      let text = this._jackpotTexts[tier];
      if (!text || text.destroyed) {
        const container = new PIXI.Container();
        container.name = `jackpot_value_${tier}`;
        text = new PIXI.Text(
          "",
          // The plates' gold `jackpots` style at the tier's size.
          new PIXI.TextStyle({
            ...scaleStyle(FONT_STYLES.jackpots, plate.FONT_SIZE),
            // Unrounded — see JACKPOT_PLATE_STROKE_BOOST.
            strokeThickness:
              (((FONT_STYLES.jackpots.strokeThickness || 0) * plate.FONT_SIZE) /
                FONT_STYLES.jackpots.fontSize) *
              JACKPOT_PLATE_STROKE_BOOST,
          }),
        );
        text.anchor.set(0.5);
        text.resolution = 2;
        text.roundPixels = false;
        container.addChild(text);
        this.spine.addSlotObject(slotName, container);
        this._jackpotTexts[tier] = text;
      }

      // const label = formatToCustomLocale(value);
      // The Royal shows its multiplier, "5000x", not the money (PO,
      // 2026-10-12) — JACKPOT_PLATE_AS_MULTIPLIER; the rest show the money.
      const multiplier = multipliers?.[tier];
      const label =
        JACKPOT_PLATE_AS_MULTIPLIER.includes(tier) && multiplier > 0
          ? `${Math.round(multiplier)}x`
          : formatToCustomLocale(value);
      if (text.text !== label) text.text = label;
      text.scale.set(1);
      if (text.width > plate.MAX_WIDTH) text.scale.set(plate.MAX_WIDTH / text.width);
    }
  }

  /**
   * The level clip for a fire stage, `logo_lvl<stage><suffix>`, falling back
   * to the nearest lower stage the rig has. A stage above LOGO_POT_STAGES
   * (WIN) uses the top stage.
   */
  _logoLevelClip(logo, stage, suffix = "") {
    const data = logo?.skeleton?.data;
    if (!data) return null;
    for (let s = Math.min(stage, LOGO_POT_STAGES); s >= 1; s--) {
      const name = `logo_lvl${s}${suffix}`;
      if (data.findAnimation(name)) return name;
    }
    return null;
  }

  /**
   * Shows fire stage `stage` (GDD "POT upgrade stages"): `logo_lvl<n>_in`
   * once, then `logo_lvl<n>` looped. Without `animate` (a reset, a room
   * opening) it goes straight to the loop. LOGO_POT_WIN: rise to the top
   * stage, then the blast.
   */
  setLogoStage(stage, animate = true) {
    const logo = this.logoSpine;
    if (!logo?.state || logo.destroyed) return;
    // WIN (GDD: the 4th stage is the WIN stage, then a small blast): the fire
    // rises to stage 4 first, then the blast. The blast waits for the rise as
    // a spine delay on its own track, not a timer.
    if (stage === LOGO_POT_WIN) {
      const from = this._logoStage ?? 1;
      if (from >= LOGO_POT_STAGES) {
        this.playLogoBlast();
        return;
      }
      const top = this._logoLevelClip(logo, LOGO_POT_STAGES);
      if (!top) {
        this.playLogoBlast();
        return;
      }
      this._logoStage = LOGO_POT_STAGES;
      const topIn = logo.skeleton.data.findAnimation(`${top}_in`);
      // From stage 1 or 2: `logo_lvl4_in_from<m>`, hand-built in logo.json
      // from the rig's one-step _in clips (same 1 s, starting on stage m's
      // pose). The trigger is the only jump; coins raise one stage at a time.
      const topJump =
        from < LOGO_POT_STAGES - 1
          ? logo.skeleton.data.findAnimation(`${top}_in_from${from}`)
          : null;
      let rise;
      // A rise still playing is not cut (_playLogoRise); the blast waits for
      // all of it.
      if (animate && from === LOGO_POT_STAGES - 1 && topIn) {
        rise = this._playLogoRise(logo, `${top}_in`, top);
      } else if (animate && topJump) {
        rise = this._playLogoRise(logo, topJump.name, top);
      } else {
        rise = this._playLogoRise(logo, null, top, animate);
      }
      this.playLogoBlast(rise);
      return;
    }
    const level = this._logoLevelClip(logo, stage);
    if (!level) return;
    // `_in` only for ONE step up — each is built from the stage below it
    // (lvl4_in starts on lvl3's fire). A jump blends straight into the loop.
    const shown = Math.min(stage, LOGO_POT_STAGES);
    const oneStepUp = shown === (this._logoStage ?? 1) + 1;
    this._logoStage = shown;
    const inClip =
      animate && oneStepUp && logo.skeleton.data.findAnimation(`${level}_in`)
        ? `${level}_in`
        : null;
    this._playLogoRise(logo, inClip, level, animate);
  }

  /**
   * Plays a fire rise on the level track: `riseClip` once, then `loopClip`
   * looped, or the loop alone (`riseClip` null). Returns the seconds until
   * the loop runs (what the WIN blast waits).
   *
   * A rise still PLAYING is never cut: the new one queues at its end and only
   * the loop queued behind it is dropped. Each _in starts on the pose the
   * previous one ends on, so they chain without a mix (see attachLogo). A
   * change that is not a rise (a reset, `animate` false) blends with
   * LEVEL_MIX on its own entry.
   */
  _playLogoRise(logo, riseClip, loopClip, animate = true) {
    const track = LOGO_ASSETS.LEVEL_TRACK;
    const state = logo.state;
    const current = state.getCurrent(track);
    const rising =
      animate &&
      riseClip &&
      current &&
      !current.loop &&
      /_in(_from\d+)?$/.test(current.animation?.name || "") &&
      current.trackTime < current.animationEnd;
    if (rising) {
      state.clearNext(current);
      const remaining = Math.max(0, current.animationEnd - current.trackTime);
      const rise = state.addAnimation(track, riseClip, false, 0);
      state.addAnimation(track, loopClip, true, 0);
      return remaining + (rise?.animation?.duration ?? 0);
    }
    if (riseClip) {
      const rise = state.setAnimation(track, riseClip, false);
      state.addAnimation(track, loopClip, true, 0);
      return rise?.animation?.duration ?? 0;
    }
    const entry = state.setAnimation(track, loopClip, true);
    // Not a rise: blend on this entry only (the level clips' own mixes are 0).
    if (entry && animate) entry.mixDuration = LOGO_ASSETS.LEVEL_MIX;
    return animate ? LOGO_ASSETS.LEVEL_MIX : 0;
  }

  /**
   * A jackpot coin landed: its plate's `jackpot_<tier>_hit` on its OWN track
   * (one per tier, so two jackpots landing together both play). The plate
   * stays lit until releaseJackpotHits. No-op when the spine lacks the clip.
   * @param {string} tier mini | minor | midi | major | grand | royal
   */
  playJackpotHit(tier) {
    const state = this.spine?.state;
    const clip = `jackpot_${tier}_hit`;
    if (!state || !this.spine.skeleton?.data?.findAnimation(clip)) return;
    const index = JACKPOT_HIT_TIERS.indexOf(tier);
    if (index < 0) return;
    const track = JACKPOT_HIT_TRACK_BASE + index;
    // A fade-out still running on this plate stops — the clip lights it again.
    this._jackpotHitFades?.get(track)?.();
    state.setAnimation(track, clip, false);
    // The clip is a 0.17 s fade-in of the plate's glow; it then holds on its
    // last frame until the next spin (releaseJackpotHits).
  }

  /**
   * The next spin: every lit plate fades back out.
   *
   * Not setEmptyAnimation: an empty mixes back to the SETUP pose, where the
   * glow slot's colour is full white, so the plate would stay lit. Instead the
   * track is cleared, the glow slots the clip keys fade to 0 on the ticker,
   * and their attachment is removed (setup has none).
   */
  releaseJackpotHits() {
    const state = this.spine?.state;
    const skeleton = this.spine?.skeleton;
    if (!state || !skeleton) return;
    if (!this._jackpotHitFades) this._jackpotHitFades = new Map();
    JACKPOT_HIT_TIERS.forEach((tier, index) => {
      const track = JACKPOT_HIT_TRACK_BASE + index;
      if (!state.tracks?.[track]) return;
      state.clearTrack(track);
      const clip = skeleton.data.findAnimation(`jackpot_${tier}_hit`);
      const slots = [
        ...new Set(
          (clip?.timelines || [])
            .map((t) => t.slotIndex)
            .filter((i) => Number.isInteger(i)),
        ),
      ]
        .map((i) => skeleton.slots[i])
        .filter(Boolean);
      if (!slots.length) return;
      const from = slots.map((slot) => slot.color.a);
      let elapsed = 0;
      const total = JACKPOT_HIT_RELEASE_S * 1000;
      const stop = () => {
        PIXI.Ticker.shared.remove(tick);
        this._jackpotHitFades.delete(track);
      };
      const tick = () => {
        elapsed += PIXI.Ticker.shared.deltaMS;
        const k = Math.max(0, 1 - elapsed / total);
        slots.forEach((slot, i) => {
          slot.color.a = from[i] * k;
        });
        if (k > 0) return;
        slots.forEach((slot) => slot.setAttachment(null));
        stop();
      };
      this._jackpotHitFades.get(track)?.();
      this._jackpotHitFades.set(track, stop);
      PIXI.Ticker.shared.add(tick);
    });
  }

  /**
   * The Pot's WIN (Hold & Win triggering): `logo_blast` once on its own track
   * above the level clip, then released. `delay` (s): how long it waits on
   * its track first (the fire's rise to stage 4, setLogoStage WIN).
   */
  playLogoBlast(delay = 0) {
    const logo = this.logoSpine;
    if (!logo?.state || logo.destroyed) return;
    const blast = LOGO_ASSETS.BLAST_ANIMATIONS.find((name) =>
      logo.skeleton.data.findAnimation(name),
    );
    if (!blast) return;
    let entry;
    if (delay > 0) {
      logo.state.clearTrack(LOGO_ASSETS.BLAST_TRACK);
      entry = logo.state.addAnimation(LOGO_ASSETS.BLAST_TRACK, blast, false, delay);
    } else {
      entry = logo.state.setAnimation(LOGO_ASSETS.BLAST_TRACK, blast, false);
    }
    logo.state.addEmptyAnimation(
      LOGO_ASSETS.BLAST_TRACK,
      0.1,
      entry?.animation?.duration ?? 0,
    );
    // Its end, for whoever sequences after it (the Random Pot). `dispose` too:
    // a blast still queued behind its delay and then cleared (a new blast's
    // clearTrack) never completes or ends; it is only disposed.
    this._logoBlastDone = new Promise((resolve) => {
      entry.listener = {
        complete: () => resolve(),
        end: () => resolve(),
        dispose: () => resolve(),
      };
    });
  }

  /** Resolves when the last blast has played out (at once if none is on). */
  logoBlastDone() {
    return this._logoBlastDone ?? Promise.resolve();
  }

  /**
   * The Random Pot's logo over the reels (GDD: "Animate the game logo over
   * the reel area, briefly covering the reels for approximately 1–2
   * seconds"): the room's `logo_reel_landscape` / `logo_reel_portrait` moves
   * the logo's slot onto the reels and back, while the logo plays
   * `logo_lvl<stage>_reel` (the same 2 s) and then returns to its level loop.
   * Resolves when the room's clip has played out — the moment the reels may
   * stop. At once when the room has no such clip.
   */
  playLogoReelFlight() {
    const state = this.spine?.state;
    const data = this.spine?.skeleton?.data;
    const portrait = (this._logoSlotName() || "").includes("portrait");
    const flight = portrait ? "logo_reel_portrait" : "logo_reel_landscape";
    if (!state || !data?.findAnimation(flight)) return Promise.resolve();

    const logo = this.logoSpine;
    const stage = Math.min(logoPotStage(), LOGO_POT_STAGES);
    const level = logo ? this._logoLevelClip(logo, stage) : null;
    const reelClip = level ? `${level}_reel` : null;
    if (logo?.state && reelClip && logo.skeleton.data.findAnimation(reelClip)) {
      logo.state.setAnimation(LOGO_ASSETS.LEVEL_TRACK, reelClip, false);
      logo.state.addAnimation(LOGO_ASSETS.LEVEL_TRACK, level, true, 0);
    }

    const entry = state.setAnimation(LOGO_REEL_TRACK, flight, false);
    state.addEmptyAnimation(LOGO_REEL_TRACK, 0.1, entry?.animation?.duration ?? 0);
    return new Promise((resolve) => {
      entry.listener = { complete: () => resolve(), end: () => resolve() };
    });
  }

  /** The background darkens (BG_DARKEN): `bg_darken_in`, held at its end. */
  darkenBackground() {
    const state = this.spine?.state;
    if (!state || this._bgDarkened) return;
    if (!this.spine.skeleton.data.findAnimation(BG_DARKEN.IN)) return;
    this._bgDarkened = true;
    state.setAnimation(BG_DARKEN.TRACK, BG_DARKEN.IN, false);
    // ── The drums, for as long as the room is dark (2026-10-22) ── on top
    // of the board fire's own sound. Stopped first: play() on a looping Howl
    // still going adds a second copy.
    // // A previous anticipation's drums still finishing their bar: no longer
    // // owed their ending — these take over, looping again.
    // const drums = HowlCache[SOUND_NAME.anticipation_drums];
    // this._drumsToken = (this._drumsToken || 0) + 1;
    // if (this._drumsEnd) drums?.off("end", this._drumsEnd);
    // this._drumsEnd = null;
    // drums?.loop(true);
    // soundManager.stop(SOUND_NAME.anticipation_drums);
    // soundManager.play(SOUND_NAME.anticipation_drums);
    // this._duckRoomForDrums();
    // The same, shared with Hold & Win's anticipation (2026-10-22).
    this._startDrums();
  }

  /**
   * The drums start, looping, and the room's music steps down under them. A
   * previous anticipation's drums still finishing their bar are no longer
   * owed their ending — these take over.
   */
  _startDrums() {
    const drums = HowlCache[SOUND_NAME.anticipation_drums];
    this._drumsToken = (this._drumsToken || 0) + 1;
    if (this._drumsEnd) drums?.off("end", this._drumsEnd);
    this._drumsEnd = null;
    drums?.loop(true);
    soundManager.stop(SOUND_NAME.anticipation_drums);
    soundManager.play(SOUND_NAME.anticipation_drums);
    // REVERTED (2026-10-22, "revert it to original", the designer's file is
    // back — the sound designer is to deliver a loop and an end hit):
    // // The intro, then the drum cycle looping from its end.
    // this._drumsLoopTimer?.clear();
    // soundManager.play(SOUND_NAME.anticipation_drums_in);
    // this._drumsLoopTimer = new TickerTimeout(() => {
    //   this._drumsLoopTimer = null;
    //   soundManager.play(SOUND_NAME.anticipation_drums);
    // }, ANTICIPATION_DRUMS.IN_MS);
    this._duckRoomForDrums();
  }

  /**
   * ── An anticipation's sounds held as ONE piece (Hold & Win, 2026-10-22) ──
   * the fire loop and the drums from the FIRST anticipation on, however many
   * respins it runs over (Vladimir: "play non stop until the end"). A second
   * start while they play changes nothing.
   */
  startAnticipationSounds() {
    if (this._anticipationSoundsOn) return;
    this._anticipationSoundsOn = true;
    soundManager.stop(SOUND_NAME.anticipation_fire);
    soundManager.play(SOUND_NAME.anticipation_fire);
    this._startDrums();
    // ...and the room darkens with them, as the base game does (2026-10-22)
    // — held across the respins the same way.
    this._darkenVisual();
  }

  /**
   * The darkness ALONE — `bg_darken_in`, held — for a room whose drums are
   * already started elsewhere (Hold & Win's startAnticipationSounds). No-op
   * if already dark or the spine has no clip.
   */
  _darkenVisual() {
    const state = this.spine?.state;
    if (!state || this._bgDarkened) return;
    if (!this.spine.skeleton.data.findAnimation(BG_DARKEN.IN)) return;
    this._bgDarkened = true;
    state.setAnimation(BG_DARKEN.TRACK, BG_DARKEN.IN, false);
  }

  /** ...and back: `bg_darken_out`, the track then released. No-op if not dark. */
  _lightenVisual() {
    const state = this.spine?.state;
    if (!state || !this._bgDarkened) return;
    this._bgDarkened = false;
    const out = this.spine.skeleton.data.findAnimation(BG_DARKEN.OUT);
    if (!out) {
      state.setEmptyAnimation(BG_DARKEN.TRACK, BG_DARKEN.MIX);
      return;
    }
    const entry = state.setAnimation(BG_DARKEN.TRACK, BG_DARKEN.OUT, false);
    entry.mixDuration = BG_DARKEN.MIX;
    state.addEmptyAnimation(BG_DARKEN.TRACK, 0, out.duration);
  }

  /** ...and their end: the fire fades, the drums finish their bar, the music comes back. */
  finishAnticipationSounds() {
    if (!this._anticipationSoundsOn) return;
    this._anticipationSoundsOn = false;
    soundManager.fadeOut(SOUND_NAME.anticipation_fire, ANTICIPATION_DRUMS.FIRE_FADE_MS);
    this._finishDrums();
    this._lightenVisual();
  }

  /** The room's music steps down under the drums (ANTICIPATION_DRUMS). */
  _duckRoomForDrums() {
    if (this._drumsDucked) return;
    const track = ANTICIPATION_DRUMS.ROOM_TRACKS.find((name) => HowlCache[name]?.playing());
    if (!track) return;
    const howl = HowlCache[track];
    howl.fade(
      howl.volume(),
      (SOUND_VOLUMES[track] ?? 1) * ANTICIPATION_DRUMS.DUCK,
      ANTICIPATION_DRUMS.DUCK_MS,
    );
    this._drumsDucked = track;
  }

  /** ...and back up — unless something else has taken it off meanwhile. */
  _restoreRoomAfterDrums() {
    const track = this._drumsDucked;
    this._drumsDucked = null;
    const howl = track ? HowlCache[track] : null;
    if (!howl?.playing()) return;
    howl.fade(howl.volume(), SOUND_VOLUMES[track] ?? 1, ANTICIPATION_DRUMS.RESTORE_MS);
  }

  /**
   * The drums end on their own: no more looping, so the bar they are on
   * finishes; then the music comes back. Silent drums: the music at once.
   */
  _finishDrums() {
    const drums = HowlCache[SOUND_NAME.anticipation_drums];
    // The designer's file again (2026-10-22): it ends its pass with its own
    // fade — so the pass finishes, then the music comes back.
    // if (!drums?.playing()) {
    //   this._restoreRoomAfterDrums();
    //   return;
    // }
    // drums.loop(false);
    // const token = this._drumsToken;
    // this._drumsEnd = () => {
    //   // A new anticipation took the drums over meanwhile.
    //   if (token !== this._drumsToken) return;
    //   this._drumsEnd = null;
    //   drums.loop(true);
    //   this._restoreRoomAfterDrums();
    // };
    // drums.once("end", this._drumsEnd);
    // ── The designer's end shot (2026-10-22, anticipation_drum_end) ── it
    // lands, the drum loop goes out under it, the music comes back over its
    // ring. Silent drums: the music at once, no shot.
    if (drums?.playing()) {
      soundManager.play(SOUND_NAME.anticipation_end);
      soundManager.fadeOut(SOUND_NAME.anticipation_drums, ANTICIPATION_DRUMS.UNDER_HIT_MS);
    }
    this._restoreRoomAfterDrums();
    // ── A volume fade, the music rising under it (2026-10-22) ── the file is
    // now its intro + the drum cycle for ~40 s (no bar to finish): "we stop
    // them when I want … fade in or out with volume". Until the end hit.
    // if (drums?.playing()) {
    //   soundManager.fadeOut(SOUND_NAME.anticipation_drums, ANTICIPATION_DRUMS.DRUMS_FADE_MS);
    // }
    // ── The end hit closes them (2026-10-22) ── "du-du DUM" lands, the
    // drums (or the intro, if it is still playing) go out under it, and the
    // music comes back over the hit's ring.
    // const introPlaying = !!this._drumsLoopTimer;
    // this._drumsLoopTimer?.clear();
    // this._drumsLoopTimer = null;
    // soundManager.play(SOUND_NAME.anticipation_end);
    // if (drums?.playing()) {
    //   soundManager.fadeOut(SOUND_NAME.anticipation_drums, ANTICIPATION_DRUMS.UNDER_HIT_MS);
    // }
    // if (introPlaying) {
    //   soundManager.fadeOutOneShot(SOUND_NAME.anticipation_drums_in, ANTICIPATION_DRUMS.UNDER_HIT_MS);
    // }
    // this._restoreRoomAfterDrums();
  }

  /** ...and back: `bg_darken_out`, then the track released. No-op if not dark. */
  lightenBackground() {
    const state = this.spine?.state;
    if (!state || !this._bgDarkened) return;
    this._bgDarkened = false;
    // The room lightens: its drums go with it.
    // soundManager.stop(SOUND_NAME.anticipation_drums);
    const out = this.spine.skeleton.data.findAnimation(BG_DARKEN.OUT);
    // // ...fading over the lighten, not cut (2026-10-22: "finish nicely").
    // soundManager.fadeOut(
    //   SOUND_NAME.anticipation_drums,
    //   (out?.duration ?? BG_DARKEN.MIX) * 1000,
    // );
    // ...finishing their bar, then the music back (ANTICIPATION_DRUMS).
    this._finishDrums();
    if (!out) {
      state.setEmptyAnimation(BG_DARKEN.TRACK, BG_DARKEN.MIX);
      return;
    }
    const entry = state.setAnimation(BG_DARKEN.TRACK, BG_DARKEN.OUT, false);
    entry.mixDuration = BG_DARKEN.MIX;
    state.addEmptyAnimation(BG_DARKEN.TRACK, 0, out.duration);
  }

  /** The cup takes a hit: `logo_hit` once on its own track, then released. */
  playLogoHit() {
    const logo = this.logoSpine;
    if (!logo?.state || logo.destroyed) return;
    const hit = LOGO_ASSETS.HIT_ANIMATIONS.find((name) =>
      logo.skeleton.data.findAnimation(name),
    );
    if (!hit) return;
    const entry = logo.state.setAnimation(LOGO_ASSETS.HIT_TRACK, hit, false);
    // Released AT the clip's end (delay = duration). Delay 0 means "duration
    // minus the mix" and would cut the glows while still bright. logo_hit
    // ends at rest, so the release there is invisible. A new hit replaces
    // this one and queues its own release.
    logo.state.addEmptyAnimation(
      LOGO_ASSETS.HIT_TRACK,
      0.1,
      entry?.animation?.duration ?? 0,
    );
  }

  // ==========================================================================
  // DORMANCY + CLEANUP
  // ==========================================================================

  /**
   * The room is hidden (another room has the screen) or shown again — its own
   * spines sleep while hidden: their clocks run, nothing is posed or rebuilt
   * (Games/Shared/spineDormancy). Set by the room's view on every swap, at
   * the wipe's midpoint, under cover.
   * @param {boolean} dormant
   */
  setDormant(dormant) {
    this._dormant = !!dormant;
    this._syncDormancy();
  }

  /** The room's OWN spines — never a board's (those manage themselves). */
  _dormancySpines() {
    return [
      this.spine,
      this.logoSpine,
      this.anticipationSpine,
      this.reelAnticipationSpine,
      this._coinCounter?.spine,
    ];
  }

  /** Re-applied whenever the room gains a spine, so a late one obeys too. */
  _syncDormancy() {
    if (this._dormant === undefined) return;
    setSpinesDormant(this._dormancySpines(), this._dormant);
  }

  destroy() {
    // Awake before anything is torn down — no sleeping entry outlives it.
    setSpinesDormant(this._dormancySpines(), false);
    if (this._coinCounter?.spine && !this._coinCounter.spine.destroyed) {
      this._coinCounter.spine.state?.clearListeners();
      this._coinCounter.spine.destroy();
    }
    this._coinCounter = null;
    // A plate still fading out must not tick on a destroyed skeleton.
    [...(this._jackpotHitFades?.values() || [])].forEach((stop) => stop());
    if (this._unregisterSlot) {
      this._unregisterSlot(this.constructor.SCENE.REEL_SET_KEY);
      this._unregisterSlot(this.constructor.SCENE.OVERLAY_REEL_SET_KEY);
    }

    this._unsubscribeLogoPot?.();
    this._unsubscribeLogoPot = null;
    if (this.logoSpine) {
      this.logoSpine.destroy();
      this.logoSpine = null;
    }

    if (this.anticipationSpine) {
      this.anticipationSpine.destroy();
      this.anticipationSpine = null;
    }

    if (this.reelAnticipationSpine) {
      this.reelAnticipationSpine.destroy();
      this.reelAnticipationSpine = null;
    }

    if (this.spine) {
      this.spine.afterUpdateWorldTransforms = null;
      this.spine.destroy();
      this.spine = null;
    }

    this.reelSlotContainer = null;
    this.overlayReelSlotContainer = null;
    this._reelBone = null;
  }
}

export default GameViewSimpleController;
